import type { RecordModel, RecordService } from "pocketbase";
import PocketBase from "pocketbase";

let BaseURL = window.location.origin.replace('v2', '');
if (BaseURL.includes("localhost") || BaseURL.includes("ngrok")) BaseURL = "https://data.baybooker.com" //"http://localhost:8090";

export const pb = new PocketBase(BaseURL);

export interface LocationRecordModel extends RecordModel {
  name?: string;
  description?: string;
  enabled?: boolean;
  address?: string;
  suburb?: string;
  city?: string;
  postcode?: string;
  country?: string;
  phone?: string;
  email?: string;
  website?: string;
	facebookLink?: string;
	instagramLink?: string;
  adminEmail?: string;
  taxID?: string;
  taxName?: string;
  taxRate?: number;
  maxBookAheadWeeks?: number;
  webshopEnabled?: boolean;
  onlineCreditCardsEnabled?: boolean;
  allowIBinPaymentRequests?: boolean;
  guestBookingsEnabled?: boolean;
  numberInPartyField?: boolean;
  // dynamic time zone
  timeZoneCode?: string;
  stdLongDateTime?: string;
  stdShortDateTime?: string;
}

export interface UserRecordModel extends RecordModel {
  username: string;
  email: string;
  emailVisibility?: boolean;
  firstName: string;
  lastName: string;
  status?: string;
  address?: string;
  postcode?: string;
  suburb?: string;
  city?: string;
  state?: string;
  country?: string;
  mobileNumber?: string;
  fob?: string;
	preferedTheme?: string;
}

export function getFullName(user: UserRecordModel | undefined) {
  if (!user) return "";

  let name = user.firstName;
  if (name) name += ` ${user.lastName}`;
  else name = user.username;

  return name;
}

export const Collections = {
	Authorigins: "_authOrigins",
	Externalauths: "_externalAuths",
	Mfas: "_mfas",
	Otps: "_otps",
	Superusers: "_superusers",
	AccessFOBs: "accessFOBs",
	AccessReaders: "accessReaders",
	Activities: "activities",
	Advertising: "advertising",
	Alarms: "alarms",
	Areas: "areas",
	AreaStatus: "areaStatus",
	BankCards: "bankCards",
	BarcodeReaders: "barcodeReaders",
	BayControlSetups: "bayControlSetups",
	BookingHistory: "bookingHistory",
	Bookings: "bookings",
	CartItemOptions: "cartItemOptions",
	CartItems: "cartItems",
	CartPayments: "cartPayments",
	Carts: "carts",
	Challenges: "challenges",
	CouponRestrictTimes: "couponRestrictTimes",
	Coupons: "coupons",
	CouponTypes: "couponTypes",
	DebugLogs: "debugLogs",
	DiscountCodes: "discountCodes",
	DocketLineOptions: "docketLineOptions",
	DocketLines: "docketLines",
	DocketLineTransactions: "docketLineTransactions",
	DocketPayments: "docketPayments",
	DocketReceipts: "docketReceipts",
	Dockets: "dockets",
	Doors: "doors",
	EventOptions: "eventOptions",
	Events: "events",
	EventTickets: "eventTickets",
	ExternalApps: "externalApps",
	Faq: "faq",
	FaqTopics: "faqTopics",
	GiftVouchers: "giftVouchers",
	GlobalSetup: "globalSetup",
	GuestUsers: "guestUsers",
	HomeAlerts: "homeAlerts",
	Integrations: "integrations",
	IoNodes: "ioNodes",
	Kiosks: "kiosks",
	Locations: "locations",
	Logs: "logs",
	MatrixDevices: "matrixDevices",
	MembershipCategories: "membershipCategories",
	Memberships: "memberships",
	Notifications: "notifications",
	OnlinePaymentRequests: "onlinePaymentRequests",
	Outputs: "outputs",
	PaymentTerminals: "paymentTerminals",
	PosSetups: "posSetups",
	PrepAreas: "prepAreas",
	PrepLines: "prepLines",
	Preps: "preps",
	Printers: "printers",
	ProductCategories: "productCategories",
	ProductOptionGroups: "productOptionGroups",
	ProductOptionsSetup: "productOptionsSetup",
	Products: "products",
	ProductsOptions: "productsOptions",
	ProductSubCategories: "productSubCategories",
	QrGenerators: "qrGenerators",
	RecurringBookings: "recurringBookings",
	RecurringEvents: "recurringEvents",
	Referrers: "referrers",
	ReportGen: "reportGen",
	ReportingGroups: "reportingGroups",
	ReportValues: "reportValues",
	Roles: "roles",
	SendQueue: "sendQueue",
	ShiftLog: "shiftLog",
	SiteComms: "siteComms",
	StaffChecklists: "staffChecklists",
	StaffChecklistSetup: "staffChecklistSetup",
	StaffLog: "staffLog",
	StockAdjustLines: "stockAdjustLines",
	StockAdjustments: "stockAdjustments",
	Suppliers: "suppliers",
	Templates: "templates",
	Times: "times",
	TODPricing: "TODPricing",
	Tracking: "tracking",
	Transactions: "transactions",
	UserCommunications: "userCommunications",
	UserMemberships: "userMemberships",
	Users: "users",
} as const
export type Collections = typeof Collections[keyof typeof Collections]

// Alias types for improved usability
export type IsoDateString = string
export type IsoAutoDateString = string & { readonly autodate: unique symbol }
export type RecordIdString = string
export type FileNameString = string & { readonly filename: unique symbol }
export type HTMLString = string

export type GeoPoint = {
	lon: number
	lat: number
}

type ExpandType<T> = unknown extends T
	? T extends unknown
		? { expand?: unknown }
		: { expand: T }
	: { expand: T }

// System fields
export type BaseSystemFields<T = unknown> = {
	id: RecordIdString
	collectionId: string
	collectionName: Collections
} & ExpandType<T>

export type AuthSystemFields<T = unknown> = {
	email: string
	emailVisibility: boolean
	username: string
	verified: boolean
} & BaseSystemFields<T>

// Record types for each collection

export type AuthoriginsRecord = {
	collectionRef: string
	created: IsoAutoDateString
	fingerprint: string
	id: string
	posDevice?: RecordIdString
	recordRef: string
	updated: IsoAutoDateString
}

export type ExternalauthsRecord = {
	collectionRef: string
	created: IsoAutoDateString
	id: string
	provider: string
	providerId: string
	recordRef: string
	updated: IsoAutoDateString
}

export type MfasRecord = {
	collectionRef: string
	created: IsoAutoDateString
	id: string
	method: string
	recordRef: string
	updated: IsoAutoDateString
}

export type OtpsRecord = {
	collectionRef: string
	created: IsoAutoDateString
	id: string
	password: string
	recordRef: string
	sentTo?: string
	updated: IsoAutoDateString
}

export type SuperusersRecord = {
	created: IsoAutoDateString
	email: string
	emailVisibility?: boolean
	id: string
	password: string
	tokenKey: string
	updated: IsoAutoDateString
	verified?: boolean
}

export type AccessFOBsRecord = {
	blocked?: boolean
	created: IsoAutoDateString
	fobNumber?: string
	id: string
	lastUsed?: IsoDateString
	updated: IsoAutoDateString
	user?: RecordIdString
}

export type AccessReadersRecord = {
	address?: number
	created: IsoAutoDateString
	door?: RecordIdString
	enabled?: boolean
	id: string
	lastFOB?: RecordIdString
	lastFOBNum?: string
	lastUser?: RecordIdString
	location?: RecordIdString
	name?: string
	sortOrder?: number
	unsetAlarm?: RecordIdString
	updated: IsoAutoDateString
}

export type ActivitiesRecord = {
	TODPricing?: RecordIdString[]
	availibleTimes?: RecordIdString[]
	bookingAgreement?: string
	bookingCanceledEmailTemplate?: RecordIdString
	bookingEmailTemplate?: RecordIdString
	bookingGap?: number
	bookingMembershipEmailTemplate?: RecordIdString
	bookingShortDescription?: string
	bookingSpacing?: number
	bookingTimeChangedTemplate?: RecordIdString
	bookingWillRemoveTemplate?: RecordIdString
	cancelAgreement?: string
	cancellationHours?: number
	changeHours?: number
	color?: string
	couponCost?: number
	couponCostAfterHours?: number
	created: IsoAutoDateString
	createdBy?: RecordIdString
	description?: string
	dontAllowMembershipDiscount?: boolean
	extID?: string
	extraPerMinFee?: number
	extraPlayersFee?: number
	futureBookingDisabled?: boolean
	guestsCanBook?: boolean
	hideTabOnBookingPage?: boolean
	id: string
	image?: FileNameString
	location?: RecordIdString
	longDescription?: string
	maxPlayers?: number
	maxVisitors?: number
	minPlayers?: number
	minVisitors?: number
	name?: string
	notes?: string
	options?: RecordIdString[]
	sessionDuration?: number
	sessionFullPrice?: number
	sessionLimit?: number
	sortOrder?: number
	updated: IsoAutoDateString
	updatedBy?: RecordIdString
	usersCanBook?: boolean
}

export type AdvertisingRecord = {
	created: IsoAutoDateString
	deviceName?: string
	enabled?: boolean
	id: string
	images?: FileNameString[]
	location?: RecordIdString
	name?: string
	showTime?: number
	sortOrder?: number
	updated: IsoAutoDateString
}

export const AlarmsStateOptions = {
	"Set": "Set",
	"Unset": "Unset",
	"Setting": "Setting",
	"Unsetting": "Unsetting",
} as const
export type AlarmsStateOptions = typeof AlarmsStateOptions[keyof typeof AlarmsStateOptions]
export type AlarmsRecord = {
	activate?: boolean
	autoSet?: boolean
	changeStateOnSecs?: number
	created: IsoAutoDateString
	enabled?: boolean
	fromCyNet?: boolean
	id: string
	input?: string
	inputIONode?: RecordIdString
	location?: RecordIdString
	maxExitDelay?: number
	name?: string
	noBookingAutoSetMins?: number
	output?: string
	outputIONode?: RecordIdString
	state?: AlarmsStateOptions
	updated: IsoAutoDateString
}

export type AreasRecord = {
	QRCodeInUse?: boolean
	accessCodeInUse?: boolean
	activities?: RecordIdString[]
	adminOnlyBookings?: boolean
	areaNumber?: number
	autoPCPowerControl?: boolean
	canLeftHanded?: boolean
	codesOnlyWhenUnattended?: boolean
	created: IsoAutoDateString
	createdBy?: RecordIdString
	description?: string
	extID?: string
	futureBookingDisabled?: boolean
	id: string
	location?: RecordIdString
	offSetMinutes?: number
	pcOnForOpenHours?: boolean
	sponsorLogo?: FileNameString
	updated: IsoAutoDateString
	updatedBy?: RecordIdString
}

export type AreaStatusRecord = {
	area?: RecordIdString
	auxInput?: boolean
	ballDispState?: string
	booking?: RecordIdString
	created: IsoAutoDateString
	downForMaintenance?: boolean
	durationRemaining?: string
	freePlay?: boolean
	id: string
	location?: RecordIdString
	nextBooking?: RecordIdString
	nextUser?: RecordIdString
	online?: boolean
	rebootPC?: boolean
	remoteMac?: string
	restartBC?: boolean
	sendMessage?: string
	showAdds?: boolean
	shutDownPC?: boolean
	startPC?: boolean
	startSession?: boolean
	stopSession?: boolean
	updated: IsoAutoDateString
	updatedOnlineState?: IsoDateString
	upgradeBC?: boolean
	user?: RecordIdString
	versionBC?: string
}

export const BankCardsTokenSourceOptions = {
	"WindCave": "WindCave",
} as const
export type BankCardsTokenSourceOptions = typeof BankCardsTokenSourceOptions[keyof typeof BankCardsTokenSourceOptions]

export const BankCardsProviderOptions = {
	"Visa": "Visa",
	"American Express": "American Express",
	"MasterCard": "MasterCard",
	"Discover": "Discover",
	"JCB": "JCB",
	"Diners Club": "Diners Club",
	"Unknown": "Unknown",
} as const
export type BankCardsProviderOptions = typeof BankCardsProviderOptions[keyof typeof BankCardsProviderOptions]
export type BankCardsRecord = {
	cardNum?: string
	created: IsoAutoDateString
	cvc?: number
	exp?: string
	id: string
	isPrimary?: boolean
	name?: string
	provider?: BankCardsProviderOptions
	token?: string
	tokenSource?: BankCardsTokenSourceOptions
	updated: IsoAutoDateString
	user?: RecordIdString
}

export const BarcodeReadersLastTypeOptions = {
	"Product": "Product",
	"Gift Voucher": "Gift Voucher",
	"Event Ticket": "Event Ticket",
	"Discount Code": "Discount Code",
	"Booking": "Booking",
	"Barcode": "Barcode",
	"Unknown": "Unknown",
	"Login Code": "Login Code",
	"Docket Number": "Docket Number",
} as const
export type BarcodeReadersLastTypeOptions = typeof BarcodeReadersLastTypeOptions[keyof typeof BarcodeReadersLastTypeOptions]
export type BarcodeReadersRecord = {
	address?: string
	created: IsoAutoDateString
	enabled?: boolean
	id: string
	lastRead?: string
	lastType?: BarcodeReadersLastTypeOptions
	location?: RecordIdString
	name?: string
	updated: IsoAutoDateString
}

export type BayControlSetupsRecord = {
	QRCodeInUse?: boolean
	accessNumber?: string
	allowDifferentActivity?: boolean
	allowMovingBays?: boolean
	blankScreenMessage?: string
	created: IsoAutoDateString
	earlyStartTime?: number
	id: string
	location?: RecordIdString
	missedBookingTime?: number
	noBookPowerDown?: number
	postBookPowerDown?: number
	preBookPowerUp?: number
	preWarnTime?: number
	projectorOffMins?: number
	showNextBooking?: boolean
	updated: IsoAutoDateString
	welcomeScreenMessage?: string
	welcomeTime?: number
}

export const BookingHistoryStateOptions = {
	"Confirmed": "Confirmed",
	"Deleted": "Deleted",
	"Pending": "Pending",
	"Processing": "Processing",
	"On Hold": "On Hold",
	"ValidatingEmail": "ValidatingEmail",
	"ValidatingSMS": "ValidatingSMS",
} as const
export type BookingHistoryStateOptions = typeof BookingHistoryStateOptions[keyof typeof BookingHistoryStateOptions]
export type BookingHistoryRecord = {
	actualDuration?: number
	area?: RecordIdString
	booking?: RecordIdString
	created: IsoAutoDateString
	createdBy?: RecordIdString
	duration?: number
	id: string
	owner?: RecordIdString
	startTime?: IsoDateString
	state?: BookingHistoryStateOptions
	updated: IsoAutoDateString
	updatedBy?: RecordIdString
	users?: RecordIdString[]
	visitors?: string
}

export const BookingsStateOptions = {
	"Pending": "Pending",
	"Processing": "Processing",
	"Confirmed": "Confirmed",
	"On Hold": "On Hold",
	"Deleted": "Deleted",
	"ValidatingEmail": "ValidatingEmail",
	"ValidatingSMS": "ValidatingSMS",
} as const
export type BookingsStateOptions = typeof BookingsStateOptions[keyof typeof BookingsStateOptions]

export const BookingsPayMethodOptions = {
	"Coupon": "Coupon",
	"Gift Voucher": "Gift Voucher",
	"POS": "POS",
	"Account Credit": "Account Credit",
	"Online": "Online",
	"Fee Waived": "Fee Waived",
} as const
export type BookingsPayMethodOptions = typeof BookingsPayMethodOptions[keyof typeof BookingsPayMethodOptions]

export const BookingsSourceOptions = {
	"Booking": "Booking",
	"Guest Booking": "Guest Booking",
	"Quick User Booking": "Quick User Booking",
	"Imported": "Imported",
	"Kiosk Booking": "Kiosk Booking",
	"Event": "Event",
} as const
export type BookingsSourceOptions = typeof BookingsSourceOptions[keyof typeof BookingsSourceOptions]
export type BookingsRecord = {
	activity?: RecordIdString
	actualDuration?: number
	actualStartTime?: IsoDateString
	area?: RecordIdString
	confirmEmailSent?: IsoDateString
	created: IsoAutoDateString
	createdBy?: RecordIdString
	deletedEmailSent?: IsoDateString
	deletedReason?: string
	docketLine?: RecordIdString
	due?: number
	duration?: number
	emailed?: IsoDateString
	endTime?: IsoDateString
	entryCode?: string
	extID?: string
	extSource?: string
	id: string
	membershipDiscount?: number
	notes?: string
	numberInParty?: number
	options?: RecordIdString[]
	owner?: RecordIdString
	paid?: number
	payMethod?: BookingsPayMethodOptions[]
	printed?: IsoDateString
	printer?: RecordIdString
	referrer?: string
	requiresEmail?: boolean
	requiresPrinting?: boolean
	source?: BookingsSourceOptions
	startTime?: IsoDateString
	state?: BookingsStateOptions
	termsNotAgreedTo?: boolean
	todDiscount?: number
	twoFACode?: string
	updated: IsoAutoDateString
	updatedBy?: RecordIdString
	users?: RecordIdString[]
	value?: number
	visitorEmail?: string
	visitorPhone?: string
	visitors?: string
	willRemoveEmailSent?: IsoDateString
}

export type CartItemOptionsRecord = {
	cartItem?: RecordIdString
	created: IsoAutoDateString
	id: string
	option?: string
	optionGroup?: string
	price?: number
	updated: IsoAutoDateString
}

export const CartItemsTransactionTypeOptions = {
	"Product": "Product",
	"UserCredit": "UserCredit",
	"Gift Voucher": "Gift Voucher",
	"Booking Payment": "Booking Payment",
	"Coupon": "Coupon",
	"Event": "Event",
	"Membership Fee": "Membership Fee",
	"User Loyalty": "User Loyalty",
} as const
export type CartItemsTransactionTypeOptions = typeof CartItemsTransactionTypeOptions[keyof typeof CartItemsTransactionTypeOptions]

export const CartItemsDiscountAppliedOptions = {
	"None": "None",
	"TOD": "TOD",
	"Membership": "Membership",
	"Code": "Code",
} as const
export type CartItemsDiscountAppliedOptions = typeof CartItemsDiscountAppliedOptions[keyof typeof CartItemsDiscountAppliedOptions]
export type CartItemsRecord = {
	booking?: RecordIdString
	cart?: RecordIdString
	cartDiscount?: number
	coupon?: RecordIdString[]
	couponQty?: number
	created: IsoAutoDateString
	createdBy?: RecordIdString
	discountApplied?: CartItemsDiscountAppliedOptions
	discountLimited?: boolean
	id: string
	itemPrice?: number
	lineDiscountTotal?: number
	lineText?: string
	lineTotal?: number
	product?: RecordIdString
	qty?: number
	sortOrder?: number
	todAdjustment?: number
	todSource?: RecordIdString
	transactionType?: CartItemsTransactionTypeOptions
	updated: IsoAutoDateString
	updatedBy?: RecordIdString
	userAccountCredit?: RecordIdString
}

export const CartPaymentsPaymentTypeOptions = {
	"Cash": "Cash",
	"Credit Card": "Credit Card",
	"Terminal": "Terminal",
	"User Account": "User Account",
	"Gift Voucher": "Gift Voucher",
	"Invoice": "Invoice",
	"Coupon": "Coupon",
	"Fee Waived": "Fee Waived",
	"Event Ticket": "Event Ticket",
	"User Loyalty": "User Loyalty",
	"Internet Banking": "Internet Banking",
} as const
export type CartPaymentsPaymentTypeOptions = typeof CartPaymentsPaymentTypeOptions[keyof typeof CartPaymentsPaymentTypeOptions]

export const CartPaymentsStatusOptions = {
	"Busy": "Busy",
	"Pending": "Pending",
	"Waiting": "Waiting",
	"Approved": "Approved",
	"Declined": "Declined",
	"Cancel": "Cancel",
	"Canceling": "Canceling",
	"Canceled": "Canceled",
	"Offline": "Offline",
	"Timeout": "Timeout",
	"Unknown": "Unknown",
	"GotWebResponse": "GotWebResponse",
	"Error": "Error",
} as const
export type CartPaymentsStatusOptions = typeof CartPaymentsStatusOptions[keyof typeof CartPaymentsStatusOptions]

export const CartPaymentsSourceOptions = {
	"Webshop": "Webshop",
	"POS": "POS",
	"Booking": "Booking",
	"Profile": "Profile",
	"Guest Booking": "Guest Booking",
	"Server": "Server",
	"Quick User Booking": "Quick User Booking",
	"Kiosk": "Kiosk",
	"Payment Request": "Payment Request",
} as const
export type CartPaymentsSourceOptions = typeof CartPaymentsSourceOptions[keyof typeof CartPaymentsSourceOptions]
export type CartPaymentsRecord = {
	OS?: string
	addCreditCard?: boolean
	amount?: number
	amtTendered?: number
	authCode?: string
	browser?: string
	btn1Text?: string
	btn2Text?: string
	btnPress?: number
	cardCvc?: number
	cardExpiry?: string
	cardFailureCode?: string
	cardName?: string
	cardNumber?: string
	cardToken?: string
	cardTransactionReff?: string
	cart?: RecordIdString
	cashOutAmount?: number
	coupon?: RecordIdString
	couponQty?: number
	created: IsoAutoDateString
	createdBy?: RecordIdString
	dataLine1?: string
	dataLine2?: string
	description?: string
	device?: string
	eventTicket?: RecordIdString
	giftVoucher?: RecordIdString
	id: string
	ipAddress?: string
	notes?: string
	paymentTerminal?: RecordIdString
	paymentType?: CartPaymentsPaymentTypeOptions
	posDevice?: RecordIdString
	source?: CartPaymentsSourceOptions
	status?: CartPaymentsStatusOptions
	surchargeAmount?: number
	tipAmount?: number
	updated: IsoAutoDateString
	updatedBy?: RecordIdString
	user?: RecordIdString
	webRedirect?: string
	webResponseLoc?: string
}

export const CartsSourceOptions = {
	"Webshop": "Webshop",
	"Server": "Server",
	"Kiosk": "Kiosk",
} as const
export type CartsSourceOptions = typeof CartsSourceOptions[keyof typeof CartsSourceOptions]

export const CartsDiscountSourcesOptions = {
	"DiscountCode": "DiscountCode",
	"Membership": "Membership",
} as const
export type CartsDiscountSourcesOptions = typeof CartsDiscountSourcesOptions[keyof typeof CartsDiscountSourcesOptions]
export type CartsRecord = {
	cartNumber?: number
	created: IsoAutoDateString
	createdBy?: RecordIdString
	description?: string
	discCodeBookings?: number
	discCodeEvents?: number
	discCodeFood?: number
	discCodeProducts?: number
	discManBookings?: number
	discManEvents?: number
	discManFood?: number
	discManProducts?: number
	discMemBookings?: number
	discMemEvents?: number
	discMemFood?: number
	discMemProducts?: number
	discountBookings?: number
	discountCode?: RecordIdString
	discountEvents?: number
	discountFood?: number
	discountProducts?: number
	discountSources?: CartsDiscountSourcesOptions[]
	docket?: RecordIdString
	due?: number
	email?: string
	foodRequiredTime?: IsoDateString
	id: string
	isTakeout?: boolean
	kioskDevice?: RecordIdString
	limit?: number
	location?: RecordIdString
	name?: string
	orderName?: string
	phone?: number
	serverProcessed?: boolean
	source?: CartsSourceOptions
	total?: number
	updated: IsoAutoDateString
	updatedBy?: RecordIdString
	user?: RecordIdString
}

export type ChallengesRecord = {
	created: IsoAutoDateString
	id: string
	name?: string
	product?: RecordIdString
	updated: IsoAutoDateString
	value?: number
	valueIncreaseRate?: number
}

export type CouponRestrictTimesRecord = {
	couponType?: RecordIdString
	created: IsoAutoDateString
	createdBy?: RecordIdString
	finishHour?: number
	finishMin?: number
	friday?: boolean
	id: string
	location?: RecordIdString
	monday?: boolean
	name?: string
	saturday?: boolean
	sortOrder?: number
	startHour?: number
	startMin?: number
	sunday?: boolean
	thursday?: boolean
	tuesday?: boolean
	updated: IsoAutoDateString
	updatedBy?: RecordIdString
	wednesday?: boolean
}

export const CouponsStatusOptions = {
	"New": "New",
	"Active": "Active",
	"Expired": "Expired",
} as const
export type CouponsStatusOptions = typeof CouponsStatusOptions[keyof typeof CouponsStatusOptions]
export type CouponsRecord = {
	activity?: RecordIdString[]
	afterHoursOnly?: boolean
	couponType?: RecordIdString
	created: IsoAutoDateString
	expired?: boolean
	expiryTime?: IsoDateString
	extID?: string
	id: string
	name?: string
	qty?: number
	qtyRemaining?: number
	status?: CouponsStatusOptions
	updated: IsoAutoDateString
	user?: RecordIdString
	validFrom?: IsoDateString
}

export const CouponTypesExpiryIntervalOptions = {
	"Days": "Days",
	"Weeks": "Weeks",
	"Months": "Months",
} as const
export type CouponTypesExpiryIntervalOptions = typeof CouponTypesExpiryIntervalOptions[keyof typeof CouponTypesExpiryIntervalOptions]
export type CouponTypesRecord = {
	activity?: RecordIdString[]
	afterHoursOnly?: boolean
	canPurchase?: boolean
	created: IsoAutoDateString
	createdBy?: RecordIdString
	expiryInterval?: CouponTypesExpiryIntervalOptions
	expiryNum?: number
	id: string
	name?: string
	purchasePrice?: number
	qty?: number
	showInWebshop?: boolean
	updated: IsoAutoDateString
	updatedBy?: RecordIdString
}

export type DebugLogsRecord = {
	browser?: string
	callingFunction?: string
	created: IsoAutoDateString
	description?: string
	error?: string
	id: string
	os?: string
	updated: IsoAutoDateString
	userIP?: string
}

export type DiscountCodesRecord = {
	created: IsoAutoDateString
	discountBookings?: number
	discountEvents?: number
	discountFood?: number
	discountProduct?: number
	email?: string
	emailed?: boolean
	expiry?: IsoDateString
	id: string
	inactive?: boolean
	labelPrinted?: boolean
	maxDiscountValue?: number
	printed?: boolean
	printer?: RecordIdString
	requiresEmail?: boolean
	requiresLabelPrint?: boolean
	requiresPrinting?: boolean
	simpleCode?: string
	singleUse?: boolean
	updated: IsoAutoDateString
	validFrom?: IsoDateString
}

export type DocketLineOptionsRecord = {
	created: IsoAutoDateString
	docketLine?: RecordIdString
	id: string
	option?: string
	optionGroup?: string
	price?: number
	updated: IsoAutoDateString
}

export const DocketLinesTransactionTypeOptions = {
	"Product": "Product",
	"UserCredit": "UserCredit",
	"Gift Voucher": "Gift Voucher",
	"Booking Payment": "Booking Payment",
	"Coupon": "Coupon",
	"Event": "Event",
	"Membership Fee": "Membership Fee",
	"User Loyalty": "User Loyalty",
} as const
export type DocketLinesTransactionTypeOptions = typeof DocketLinesTransactionTypeOptions[keyof typeof DocketLinesTransactionTypeOptions]

export const DocketLinesDiscountAppliedOptions = {
	"None": "None",
	"TOD": "TOD",
	"Line": "Line",
	"Membership": "Membership",
	"Docket Manual": "Docket Manual",
	"Code": "Code",
} as const
export type DocketLinesDiscountAppliedOptions = typeof DocketLinesDiscountAppliedOptions[keyof typeof DocketLinesDiscountAppliedOptions]

export const DocketLinesStatusOptions = {
	"Available": "Available",
	"Processing": "Processing",
	"Processed": "Processed",
} as const
export type DocketLinesStatusOptions = typeof DocketLinesStatusOptions[keyof typeof DocketLinesStatusOptions]
export type DocketLinesRecord = {
	booking?: RecordIdString
	coupon?: RecordIdString[]
	couponQty?: number
	couponType?: RecordIdString
	created: IsoAutoDateString
	createdBy?: RecordIdString
	discountApplied?: DocketLinesDiscountAppliedOptions
	discountLimited?: boolean
	docket?: RecordIdString
	docketDiscount?: number
	eventTicket?: RecordIdString[]
	giftVoucher?: RecordIdString[]
	id: string
	itemPrice?: number
	lineDiscount?: number
	lineDiscountTotal?: number
	lineText?: string
	lineTotal?: number
	processed?: number
	processing?: number
	product?: RecordIdString
	qty?: number
	sortOrder?: number
	status?: DocketLinesStatusOptions
	todAdjustment?: number
	todSource?: RecordIdString
	transactionType?: DocketLinesTransactionTypeOptions
	updated: IsoAutoDateString
	updatedBy?: RecordIdString
	userAccountCredit?: RecordIdString
}

export type DocketLineTransactionsRecord = {
	balance?: number
	created: IsoAutoDateString
	docketLine?: RecordIdString
	id: string
	kioskDevice?: RecordIdString
	notes?: string
	posDevice?: RecordIdString
	prepped?: number
	qtyChange?: number
	serverPerson?: RecordIdString
	updated: IsoAutoDateString
}

export const DocketPaymentsPaymentTypeOptions = {
	"Cash": "Cash",
	"Credit Card": "Credit Card",
	"Terminal": "Terminal",
	"User Account": "User Account",
	"Gift Voucher": "Gift Voucher",
	"Invoice": "Invoice",
	"Coupon": "Coupon",
	"Fee Waived": "Fee Waived",
	"Event Ticket": "Event Ticket",
	"User Loyalty": "User Loyalty",
	"Internet Banking": "Internet Banking",
} as const
export type DocketPaymentsPaymentTypeOptions = typeof DocketPaymentsPaymentTypeOptions[keyof typeof DocketPaymentsPaymentTypeOptions]

export const DocketPaymentsStatusOptions = {
	"Busy": "Busy",
	"Pending": "Pending",
	"Waiting": "Waiting",
	"Approved": "Approved",
	"Declined": "Declined",
	"Cancel": "Cancel",
	"Canceling": "Canceling",
	"Canceled": "Canceled",
	"Offline": "Offline",
	"Timeout": "Timeout",
	"Unknown": "Unknown",
	"GotWebResponse": "GotWebResponse",
	"Error": "Error",
} as const
export type DocketPaymentsStatusOptions = typeof DocketPaymentsStatusOptions[keyof typeof DocketPaymentsStatusOptions]

export const DocketPaymentsSourceOptions = {
	"Webshop": "Webshop",
	"POS": "POS",
	"Booking": "Booking",
	"Profile": "Profile",
	"Guest Booking": "Guest Booking",
	"Server": "Server",
	"Quick User Booking": "Quick User Booking",
	"Kiosk": "Kiosk",
	"Payment Request": "Payment Request",
} as const
export type DocketPaymentsSourceOptions = typeof DocketPaymentsSourceOptions[keyof typeof DocketPaymentsSourceOptions]
export type DocketPaymentsRecord = {
	OS?: string
	addCreditCard?: boolean
	amount?: number
	amtTendered?: number
	authCode?: string
	browser?: string
	btn1Text?: string
	btn2Text?: string
	btnPress?: number
	cardCvc?: number
	cardExpiry?: string
	cardFailureCode?: string
	cardName?: string
	cardNumber?: string
	cardToken?: string
	cardTransactionReff?: string
	cartPaymentId?: string
	cashOutAmount?: number
	coupon?: RecordIdString
	couponQty?: number
	created: IsoAutoDateString
	createdBy?: RecordIdString
	dataLine1?: string
	dataLine2?: string
	description?: string
	device?: string
	docket?: RecordIdString
	eventTicket?: RecordIdString
	giftVoucher?: RecordIdString
	id: string
	ipAddress?: string
	notes?: string
	paymentTerminal?: RecordIdString
	paymentType?: DocketPaymentsPaymentTypeOptions
	posDevice?: RecordIdString
	source?: DocketPaymentsSourceOptions
	status?: DocketPaymentsStatusOptions
	surchargeAmount?: number
	tipAmount?: number
	updated: IsoAutoDateString
	updatedBy?: RecordIdString
	user?: RecordIdString
	webRedirect?: string
	webResponseLoc?: string
}

export type DocketReceiptsRecord = {
	created: IsoAutoDateString
	docketID?: RecordIdString
	email?: string
	emailed?: boolean
	id: string
	officeInvoiceEmailed?: boolean
	openCashDraw?: boolean
	printed?: boolean
	printer?: RecordIdString
	requiresEmail?: boolean
	requiresOfficeInvoice?: boolean
	requiresPrinting?: boolean
	updated: IsoAutoDateString
}

export const DocketsStatusOptions = {
	"Open": "Open",
	"Partial": "Partial",
	"Paid": "Paid",
	"Closed": "Closed",
} as const
export type DocketsStatusOptions = typeof DocketsStatusOptions[keyof typeof DocketsStatusOptions]

export const DocketsSourceOptions = {
	"Webshop": "Webshop",
	"POS": "POS",
	"Booking": "Booking",
	"Profile": "Profile",
	"Server": "Server",
	"Guest Booking": "Guest Booking",
	"Quick User Booking": "Quick User Booking",
	"Kiosk": "Kiosk",
} as const
export type DocketsSourceOptions = typeof DocketsSourceOptions[keyof typeof DocketsSourceOptions]

export const DocketsDiscountSourcesOptions = {
	"DiscountCode": "DiscountCode",
	"POS": "POS",
	"Membership": "Membership",
} as const
export type DocketsDiscountSourcesOptions = typeof DocketsDiscountSourcesOptions[keyof typeof DocketsDiscountSourcesOptions]

export const DocketsPrepTimeOptions = {
	"Docket Leave": "Docket Leave",
	"When Paid": "When Paid",
	"Leave Prompt": "Leave Prompt",
} as const
export type DocketsPrepTimeOptions = typeof DocketsPrepTimeOptions[keyof typeof DocketsPrepTimeOptions]
export type DocketsRecord = {
	created: IsoAutoDateString
	createdBy?: RecordIdString
	description?: string
	device?: string
	discCodeBookings?: number
	discCodeEvents?: number
	discCodeFood?: number
	discCodeProducts?: number
	discManBookings?: number
	discManEvents?: number
	discManFood?: number
	discManProducts?: number
	discMemBookings?: number
	discMemEvents?: number
	discMemFood?: number
	discMemProducts?: number
	discount?: number
	discountBookings?: number
	discountCode?: RecordIdString
	discountEvents?: number
	discountFood?: number
	discountLimit?: number
	discountProducts?: number
	discountSources?: DocketsDiscountSourcesOptions[]
	docketNumber?: number
	due?: number
	email?: string
	foodRequiredTime?: IsoDateString
	groupOrder?: boolean
	id: string
	isTakeout?: boolean
	kioskDevice?: RecordIdString
	limit?: number
	location?: RecordIdString
	name?: string
	orderName?: string
	phone?: number
	posDevice?: RecordIdString
	prepRequiresPrint?: boolean
	prepTime?: DocketsPrepTimeOptions
	prepareOrder?: boolean
	printList?: boolean
	serverPerson?: RecordIdString
	serverProcessed?: boolean
	source?: DocketsSourceOptions
	status?: DocketsStatusOptions
	subTotal?: number
	total?: number
	updated: IsoAutoDateString
	updatedBy?: RecordIdString
	user?: RecordIdString
}

export const DoorsStateOptions = {
	"Unknown": "Unknown",
	"Locked": "Locked",
	"Unlocked": "Unlocked",
	"Open-Locked": "Open-Locked",
	"Open-Unlocked": "Open-Unlocked",
	"Closed-Locked": "Closed-Locked",
	"Closed-Unlocked": "Closed-Unlocked",
	"Open": "Open",
	"Closed": "Closed",
} as const
export type DoorsStateOptions = typeof DoorsStateOptions[keyof typeof DoorsStateOptions]
export type DoorsRecord = {
	activate?: boolean
	afterBookingMins?: number
	allMemberships?: boolean
	allUsers?: boolean
	beforeBookingMins?: number
	bookingActivityAccess?: RecordIdString[]
	created: IsoAutoDateString
	enablePhoneGeoAccess?: boolean
	enableQRAccess?: boolean
	enabled?: boolean
	fromCyNet?: boolean
	geoLocation?: GeoPoint
	geoMaxDistance?: number
	holdActivated?: boolean
	holdOutput?: string
	id: string
	input?: string
	inputIONode?: RecordIdString
	location?: RecordIdString
	membersRequireBooking?: boolean
	membershipaccess?: RecordIdString[]
	name?: string
	onSeconds?: number
	output?: string
	outputIONode?: RecordIdString
	roles?: RecordIdString[]
	sortOrder?: number
	state?: DoorsStateOptions
	updated: IsoAutoDateString
}

export const EventOptionsTypeOptions = {
	"Number": "Number",
	"Text": "Text",
	"Selection": "Selection",
} as const
export type EventOptionsTypeOptions = typeof EventOptionsTypeOptions[keyof typeof EventOptionsTypeOptions]
export type EventOptionsRecord = {
	created: IsoAutoDateString
	event?: RecordIdString
	hintText?: string
	id: string
	name?: string
	prompt?: string
	required?: boolean
	selections?: string
	type?: EventOptionsTypeOptions
	updated: IsoAutoDateString
}

export const EventsStateOptions = {
	"Pending": "Pending",
	"Confirmed": "Confirmed",
	"Deleted": "Deleted",
} as const
export type EventsStateOptions = typeof EventsStateOptions[keyof typeof EventsStateOptions]
export type EventsRecord = {
	bookings?: RecordIdString[]
	created: IsoAutoDateString
	description?: string
	id: string
	location?: RecordIdString
	moreInfoUrl?: string
	name?: string
	state?: EventsStateOptions
	trackTickets?: boolean
	updated: IsoAutoDateString
}

export const EventTicketsStatusOptions = {
	"New": "New",
	"Active": "Active",
	"Expired": "Expired",
} as const
export type EventTicketsStatusOptions = typeof EventTicketsStatusOptions[keyof typeof EventTicketsStatusOptions]
export type EventTicketsRecord<Toptions = unknown> = {
	adminNotes?: string
	barRemaining?: number
	barValue?: number
	created: IsoAutoDateString
	docketLine?: RecordIdString
	due?: number
	email?: string
	emailed?: boolean
	event?: RecordIdString
	eventDate?: IsoDateString
	id: string
	labelPrinted?: boolean
	name?: string
	options?: null | Toptions
	paid?: boolean
	phone?: string
	printed?: boolean
	printer?: RecordIdString
	productRecord?: RecordIdString
	requiresEmail?: boolean
	requiresLabelPrint?: boolean
	requiresPrinting?: boolean
	scannedTime?: IsoDateString
	status?: EventTicketsStatusOptions
	total?: number
	updated: IsoAutoDateString
	used?: boolean
	user?: RecordIdString
}

export type ExternalAppsRecord = {
	commandLineArgs?: string
	created: IsoAutoDateString
	description?: string
	fileName?: string
	id: string
	name?: string
	updated: IsoAutoDateString
}

export type FaqRecord = {
	adminOnly?: boolean
	answer?: string
	created: IsoAutoDateString
	id: string
	question?: string
	topic?: RecordIdString
	updated: IsoAutoDateString
}

export type FaqTopicsRecord = {
	created: IsoAutoDateString
	description?: string
	icon?: string
	id: string
	name?: string
	updated: IsoAutoDateString
}

export const GiftVouchersStatusOptions = {
	"New": "New",
	"Active": "Active",
	"Expired": "Expired",
} as const
export type GiftVouchersStatusOptions = typeof GiftVouchersStatusOptions[keyof typeof GiftVouchersStatusOptions]
export type GiftVouchersRecord = {
	balanceRemaining?: number
	created: IsoAutoDateString
	createdBy?: RecordIdString
	docketLine?: RecordIdString
	dontShowValue?: boolean
	email?: string
	emailed?: boolean
	expiry?: IsoDateString
	extId?: string
	extSource?: string
	from?: string
	id: string
	labelPrinted?: boolean
	message?: string
	originalAmount?: number
	paid?: boolean
	printed?: boolean
	printer?: RecordIdString
	requiresEmail?: boolean
	requiresLabelPrint?: boolean
	requiresPrinting?: boolean
	status?: GiftVouchersStatusOptions
	to?: string
	updated: IsoAutoDateString
	updatedBy?: RecordIdString
	void?: boolean
}

export const GlobalSetupDefaultThemeOptions = {
	"Light": "Light",
	"Dark": "Dark",
	"System": "System",
} as const
export type GlobalSetupDefaultThemeOptions = typeof GlobalSetupDefaultThemeOptions[keyof typeof GlobalSetupDefaultThemeOptions]

export const GlobalSetupCurrencyOptions = {
	"NZD": "NZD",
	"AUD": "AUD",
} as const
export type GlobalSetupCurrencyOptions = typeof GlobalSetupCurrencyOptions[keyof typeof GlobalSetupCurrencyOptions]
export type GlobalSetupRecord = {
	SubDomain?: string
	addCreditDialogDesc?: string
	adminAddedUserEmailTemplate?: RecordIdString
	areaPlural?: string
	areaSingular?: string
	backGroundImage?: FileNameString
	bookingTicketTerms?: string
	buyCouponsDialogDesc?: string
	buyEventVoucherDesc?: string
	buyEventsDesc?: string
	buyGiftVoucherDialogDesc?: string
	buyMembershipDialogDesc?: string
	buyMembershipTerms?: string
	companyName?: string
	couponValue?: number
	created: IsoAutoDateString
	currency?: GlobalSetupCurrencyOptions
	defaultHomeLocation?: RecordIdString
	defaultTheme?: GlobalSetupDefaultThemeOptions
	discountCodeTerms?: string
	dontDailySettleAccounts?: boolean
	eventVoucherTerms?: string
	failedPaymentEmailTemplate?: RecordIdString
	failedPaymentStopEmailTemplate?: RecordIdString
	generalTermsAndConditions?: HTMLString
	generalTermsAndConditionsEditedDate?: IsoDateString
	giftVoucherExpiryMonths?: number
	giftVoucherTerms?: string
	googleAnalyticsId?: string
	googleTagManagerId?: string
	headerIcon?: FileNameString
	headerIconDark?: FileNameString
	headerLogo?: FileNameString
	headerLogoDark?: FileNameString
	homePageImage?: FileNameString
	homePageImageDark?: FileNameString
	id: string
	importedMembershipEmailTemplate?: RecordIdString
	importedUserEmailTemplate?: RecordIdString
	logo?: FileNameString
	logoDark?: FileNameString
	maxDocketDiscount?: number
	maxDocketLoyalty?: number
	maxPosLineDiscount?: number
	membershipTermsAndConditions?: HTMLString
	membershipTermsAndConditionsEditedDate?: IsoDateString
	metaPixelId?: string
	moduleBookings?: boolean
	modulePOS?: boolean
	newMembershipEmailTemplate?: RecordIdString
	newUserEmailTemplate?: RecordIdString
	oldMemberPlatformName?: string
	oldMemberPlatformUrl?: string
	quitMembershipEmailTemplate?: RecordIdString
	redeemGiftDialogDesc?: string
	requireLRHandPreferance?: boolean
	requireMembershipMobile?: boolean
	signInLogo?: FileNameString
	signInLogoDark?: FileNameString
	signUpImage?: FileNameString
	signUpTerms?: string
	taxName?: string
	taxRate?: number
	themePrimaryColor?: string
	themePrimaryColorDark?: string
	transferCreditDialogDesc?: string
	transitionMembers?: boolean
	updated: IsoAutoDateString
	useNationalBodyID?: boolean
	useNewsletters?: boolean
	validateMobileNumbers?: boolean
}

export type GuestUsersRecord = {
	askedAboutNewsletters?: boolean
	created: IsoAutoDateString
	email?: string
	id: string
	mailchimpSyncError?: boolean
	mobileNumber?: string
	name?: string
	subscribed?: boolean
	updated: IsoAutoDateString
	user?: RecordIdString
}

export const HomeAlertsTypeOptions = {
	"Info": "Info",
	"Warning": "Warning",
	"Error": "Error",
} as const
export type HomeAlertsTypeOptions = typeof HomeAlertsTypeOptions[keyof typeof HomeAlertsTypeOptions]
export type HomeAlertsRecord = {
	color?: string
	created: IsoAutoDateString
	endTime?: IsoDateString
	id: string
	location?: RecordIdString
	message?: string
	startTime?: IsoDateString
	type?: HomeAlertsTypeOptions
	updated: IsoAutoDateString
}

export type IntegrationsRecord<TmailchimpAvailableAudiences = unknown, TreckonAvailableBooks = unknown> = {
	created: IsoAutoDateString
	googleAnalyticsId?: string
	googleTagManagerId?: string
	id: string
	mailchimpAccessToken?: string
	mailchimpAudienceId?: string
	mailchimpAvailableAudiences?: null | TmailchimpAvailableAudiences
	mailchimpServerPrefix?: string
	mailchimpStatus?: string
	mailchimpWebhookSecret?: string
	metaPixelId?: string
	reckonAccessToken?: string
	reckonAvailableBooks?: null | TreckonAvailableBooks
	reckonExpiresAt?: IsoDateString
	reckonRefreshToken?: string
	reckonStatus?: string
	updated: IsoAutoDateString
	xeroAccessToken?: string
	xeroExpiresAtUtc?: string
	xeroRefreshToken?: string
	xeroTenantId?: string
}

export const IoNodesNodeTypeOptions = {
	"CyNet": "CyNet",
	"Modbus": "Modbus",
} as const
export type IoNodesNodeTypeOptions = typeof IoNodesNodeTypeOptions[keyof typeof IoNodesNodeTypeOptions]
export type IoNodesRecord = {
	NodeType?: IoNodesNodeTypeOptions
	clientID?: string
	created: IsoAutoDateString
	description?: string
	id: string
	location?: RecordIdString
	name?: string
	serverHost?: string
	serverPort?: number
	siteGUID?: string
	updated: IsoAutoDateString
	userGUID?: string
}

export type KiosksRecord = {
	accessReader?: RecordIdString
	allowTakeout?: boolean
	barCodeReader?: RecordIdString
	bookingsImage?: FileNameString
	canMakeBookings?: boolean
	canPayAtPOS?: boolean
	canSellEvents?: boolean
	canSellVouchers?: boolean
	created: IsoAutoDateString
	dontShowSoldOut?: boolean
	eventsImage?: FileNameString
	field?: boolean
	foodDrinksImage?: FileNameString
	giftVouchersImage?: FileNameString
	headerImage?: FileNameString
	homeImage?: FileNameString
	id: string
	location?: RecordIdString
	name?: string
	openTabMode?: boolean
	paymentTerminal?: RecordIdString
	receiptPrinter?: RecordIdString
	showOrderName?: boolean
	tabDocket?: RecordIdString
	updated: IsoAutoDateString
}

export type LocationsRecord = {
	address?: string
	adminEmail?: string
	allowIBinPaymentRequests?: boolean
	allowUserNegAccountBalance?: boolean
	amountsIncludeTax?: boolean
	autoMembership?: RecordIdString
	autoRemoveUnpaidBookings?: boolean
	bankDetails?: HTMLString
	bookingFoodLinkCatID?: RecordIdString
	cardSurchargeFixedFee?: number
	cardSurchargePercentFee?: number
	city?: string
	country?: string
	created: IsoAutoDateString
	defaultPosSetup?: RecordIdString
	description?: string
	email?: string
	enabled?: boolean
	facebookLink?: string
	failedPaymentStopBenefitsCount?: number
	giftVoucherExpiryMonths?: number
	guestBookingsEnabled?: boolean
	guestValidateEmail?: boolean
	guestValidateSMS?: boolean
	id: string
	instagramLink?: string
	kioskPayAtPosEnabled?: boolean
	mainSitePC?: string
	maxBookAheadWeeks?: number
	maxFoodOrderAheadDays?: number
	maxServerDailySettleLimit?: number
	mustPayBeforePlay?: boolean
	name?: string
	numberInPartyField?: boolean
	onlineCreditCardsEnabled?: boolean
	onlineDirectDebitEnabled?: boolean
	onlineShopping?: boolean
	payInStoreEnabled?: boolean
	phone?: string
	posMaxItemsWidth?: number
	postcode?: string
	quickBookEventActivities?: RecordIdString[]
	requireInduction?: boolean
	stdLongDateTime?: string
	stdShortDateTime?: string
	suburb?: string
	taxID?: string
	taxName?: string
	taxRate?: number
	timeZoneCode?: string
	updated: IsoAutoDateString
	usesPrepScreens?: boolean
	webshopEnabled?: boolean
	website?: string
}

export const LogsTypeOptions = {
	"Access": "Access",
	"Scan": "Scan",
} as const
export type LogsTypeOptions = typeof LogsTypeOptions[keyof typeof LogsTypeOptions]

export const LogsResultOptions = {
	"Access Granted": "Access Granted",
	"Access Blocked": "Access Blocked",
	"Read OK": "Read OK",
	"No User": "No User",
	"Incorrect Time": "Incorrect Time",
	"Door Disabled": "Door Disabled",
	"Incorrect Geo Location": "Incorrect Geo Location",
	"Incorrect Activity": "Incorrect Activity",
	"Booking Access Granted": "Booking Access Granted",
} as const
export type LogsResultOptions = typeof LogsResultOptions[keyof typeof LogsResultOptions]
export type LogsRecord = {
	created: IsoAutoDateString
	description?: string
	fob?: RecordIdString
	fobNum?: string
	geoUserLocation?: GeoPoint
	id: string
	location?: RecordIdString
	reader?: RecordIdString
	result?: LogsResultOptions
	type?: LogsTypeOptions
	updated: IsoAutoDateString
	user?: RecordIdString
}

export type MatrixDevicesRecord = {
	created: IsoAutoDateString
	currentSource?: RecordIdString
	display?: boolean
	id: string
	ipAddress?: string
	location?: RecordIdString
	name?: string
	sortOrder?: number
	source?: boolean
	updated: IsoAutoDateString
}

export type MembershipCategoriesRecord = {
	created: IsoAutoDateString
	createdBy?: RecordIdString
	description?: string
	id: string
	location?: RecordIdString
	name?: string
	sortOrder?: number
	updated: IsoAutoDateString
	updatedBy?: RecordIdString
}

export const MembershipsRenewIntervalOptions = {
	"Days": "Days",
	"Weeks": "Weeks",
	"Months": "Months",
	"Unlimited": "Unlimited",
} as const
export type MembershipsRenewIntervalOptions = typeof MembershipsRenewIntervalOptions[keyof typeof MembershipsRenewIntervalOptions]
export type MembershipsRecord<Tbenefits = unknown> = {
	LoyaltyBookings?: number
	LoyaltyEvents?: number
	LoyaltyFood?: number
	LoyaltyProduct?: number
	activities?: RecordIdString[]
	benefits?: null | Tbenefits
	cantUseMembershipCoupons?: boolean
	category?: RecordIdString
	color?: string
	conditions?: string
	couponsIncluded?: RecordIdString[]
	created: IsoAutoDateString
	darkimage?: FileNameString
	description?: string
	discountEvents?: number
	discountFood?: number
	discountFurtherBookings?: number
	discountProduct?: number
	extID?: string
	goLiveDate?: IsoDateString
	id: string
	image?: FileNameString
	joinCoupons?: RecordIdString[]
	joinFee?: number
	location?: RecordIdString
	membershipLimit?: number
	minTerm?: number
	name?: string
	publicCanPurchase?: boolean
	recouringFee?: number
	renewInterval?: MembershipsRenewIntervalOptions
	renewTerm?: number
	showPlusTax?: boolean
	sortOrder?: number
	updated: IsoAutoDateString
	userCount?: number
}

export type NotificationsRecord = {
	_allBookingsCanceled?: number
	_allBookingsCanceledActivities?: RecordIdString[]
	_allBookingsTimeChanged?: number
	_allBookingsTimeChangedActivities?: RecordIdString[]
	_allFailedPayments?: number
	_allMemberBenefitHolds?: number
	_allMembershipsCanceled?: number
	_allNewBookings?: number
	_allNewBookingsActivities?: RecordIdString[]
	_allNewEventTickets?: number
	_allNewMemberships?: number
	_allNewSignUps?: number
	_dailyReport?: number
	_monthlyReport?: number
	_weeklyReport?: number
	bookingStartsSoon?: number
	created: IsoAutoDateString
	id: string
	newBooking?: number
	newsletters?: number
	updated: IsoAutoDateString
	user?: RecordIdString
}

export const OnlinePaymentRequestsStatusOptions = {
	"Requested": "Requested",
	"Approved": "Approved",
	"User Declined": "User Declined",
	"Admin Canceled": "Admin Canceled",
	"System Canceled": "System Canceled",
	"Paid Via IB": "Paid Via IB",
} as const
export type OnlinePaymentRequestsStatusOptions = typeof OnlinePaymentRequestsStatusOptions[keyof typeof OnlinePaymentRequestsStatusOptions]
export type OnlinePaymentRequestsRecord = {
	amount?: number
	created: IsoAutoDateString
	createdBy?: RecordIdString
	docket?: RecordIdString
	email?: string
	emailed?: boolean
	id: string
	paidAdminEmailed?: boolean
	status?: OnlinePaymentRequestsStatusOptions
	updated: IsoAutoDateString
}

export const OutputsModeOptions = {
	"booking": "booking",
} as const
export type OutputsModeOptions = typeof OutputsModeOptions[keyof typeof OutputsModeOptions]

export const OutputsStateOptions = {
	"On": "On",
	"Off": "Off",
	"Unknown": "Unknown",
} as const
export type OutputsStateOptions = typeof OutputsStateOptions[keyof typeof OutputsStateOptions]
export type OutputsRecord = {
	activate?: boolean
	areas?: RecordIdString[]
	created: IsoAutoDateString
	enabled?: boolean
	fromCyNet?: boolean
	id: string
	location?: RecordIdString
	minsAfter?: number
	minsBefore?: number
	mode?: OutputsModeOptions
	name?: string
	onOpenHours?: boolean
	output?: string
	outputIONode?: RecordIdString
	sortOrder?: number
	state?: OutputsStateOptions
	updated: IsoAutoDateString
}

export const PaymentTerminalsProviderOptions = {
	"Stripe": "Stripe",
	"Windcave": "Windcave",
	"Manual": "Manual",
	"Verifone": "Verifone",
	"VerifoneVx": "VerifoneVx",
} as const
export type PaymentTerminalsProviderOptions = typeof PaymentTerminalsProviderOptions[keyof typeof PaymentTerminalsProviderOptions]

export const PaymentTerminalsTypeOptions = {
	"EFTPOS": "EFTPOS",
	"Phone": "Phone",
	"Test": "Test",
} as const
export type PaymentTerminalsTypeOptions = typeof PaymentTerminalsTypeOptions[keyof typeof PaymentTerminalsTypeOptions]
export type PaymentTerminalsRecord = {
	canRefund?: boolean
	cashOut?: boolean
	created: IsoAutoDateString
	createdBy?: RecordIdString
	description?: string
	enabled?: boolean
	id: string
	location?: RecordIdString
	manualCancel?: boolean
	merchantID?: string
	merchantName?: string
	pairResult?: string
	pairTerminal?: boolean
	provider?: PaymentTerminalsProviderOptions
	terminalID?: string
	terminalNumber?: number
	type?: PaymentTerminalsTypeOptions
	updated: IsoAutoDateString
	updatedBy?: RecordIdString
}

export const PosSetupsPrepTimeOptions = {
	"Docket Leave": "Docket Leave",
	"When Paid": "When Paid",
	"Leave Prompt": "Leave Prompt",
} as const
export type PosSetupsPrepTimeOptions = typeof PosSetupsPrepTimeOptions[keyof typeof PosSetupsPrepTimeOptions]
export type PosSetupsRecord = {
	accessReader?: RecordIdString
	barCodeReader?: RecordIdString
	cashInTill?: number
	created: IsoAutoDateString
	hasCashDraw?: boolean
	id: string
	location?: RecordIdString
	name?: string
	paymentTerminal?: RecordIdString
	prepTime?: PosSetupsPrepTimeOptions
	receiptPrinter?: RecordIdString
	requireServerPerson?: boolean
	showPrepButton?: boolean
	swipeTimeoutMins?: number
	updated: IsoAutoDateString
}

export const PrepAreasReqTimeStepsOptions = {
	"E1": "1",
	"E2": "2",
	"E5": "5",
	"E10": "10",
	"E15": "15",
	"E20": "20",
	"E30": "30",
} as const
export type PrepAreasReqTimeStepsOptions = typeof PrepAreasReqTimeStepsOptions[keyof typeof PrepAreasReqTimeStepsOptions]
export type PrepAreasRecord = {
	allowTakeout?: boolean
	autoPrint?: boolean
	autoPrintCopies?: number
	created: IsoAutoDateString
	currentWaitMins?: number
	description?: string
	dontPrintReqTime?: boolean
	excludeKioskDevices?: RecordIdString[]
	excludePosDevices?: RecordIdString[]
	id: string
	location?: RecordIdString
	name?: string
	notifySound?: FileNameString
	oneLinePerPrint?: boolean
	printer?: RecordIdString
	reqTimeSteps?: PrepAreasReqTimeStepsOptions
	updated: IsoAutoDateString
	usingScreen?: boolean
}

export const PrepLinesStatusOptions = {
	"Canceled": "Canceled",
	"Changed": "Changed",
	"Original": "Original",
} as const
export type PrepLinesStatusOptions = typeof PrepLinesStatusOptions[keyof typeof PrepLinesStatusOptions]
export type PrepLinesRecord = {
	created: IsoAutoDateString
	docketLine?: RecordIdString
	docketLineQty?: number
	id: string
	prep?: RecordIdString
	status?: PrepLinesStatusOptions
	updated: IsoAutoDateString
}

export const PrepsStatusOptions = {
	"New": "New",
	"In Progress": "In Progress",
	"Ready": "Ready",
	"Done": "Done",
	"Canceled": "Canceled",
	"Changed": "Changed",
} as const
export type PrepsStatusOptions = typeof PrepsStatusOptions[keyof typeof PrepsStatusOptions]
export type PrepsRecord = {
	created: IsoAutoDateString
	description?: string
	docket?: RecordIdString
	id: string
	inProgressTime?: IsoDateString
	isTakeout?: boolean
	prepArea?: RecordIdString
	prepNum?: number
	prepSufix?: number
	printed?: boolean
	readyTime?: IsoDateString
	requiredTime?: IsoDateString
	requiresPrint?: boolean
	status?: PrepsStatusOptions
	updated: IsoAutoDateString
}

export const PrintersDriverTypeOptions = {
	"Windows": "Windows",
	"Network": "Network",
} as const
export type PrintersDriverTypeOptions = typeof PrintersDriverTypeOptions[keyof typeof PrintersDriverTypeOptions]

export const PrintersPrinterTypeOptions = {
	"Standard": "Standard",
	"Docket": "Docket",
	"Label": "Label",
} as const
export type PrintersPrinterTypeOptions = typeof PrintersPrinterTypeOptions[keyof typeof PrintersPrinterTypeOptions]
export type PrintersRecord = {
	created: IsoAutoDateString
	description?: string
	driverType?: PrintersDriverTypeOptions
	id: string
	location?: RecordIdString
	name?: string
	networkAddress?: string
	pcName?: string
	printerName?: string
	printerType?: PrintersPrinterTypeOptions
	updated: IsoAutoDateString
}

export const ProductCategoriesItemTypeOptions = {
	"Event": "Event",
	"Voucher": "Voucher",
	"Product": "Product",
} as const
export type ProductCategoriesItemTypeOptions = typeof ProductCategoriesItemTypeOptions[keyof typeof ProductCategoriesItemTypeOptions]
export type ProductCategoriesRecord = {
	TODPricing?: RecordIdString[]
	created: IsoAutoDateString
	createdBy?: RecordIdString
	icon?: string
	id: string
	image?: FileNameString
	isFood?: boolean
	isVoucher?: boolean
	itemType?: ProductCategoriesItemTypeOptions
	location?: RecordIdString
	name?: string
	prepArea?: RecordIdString
	reportingGroup?: RecordIdString
	sortOrder?: number
	updated: IsoAutoDateString
	updatedBy?: RecordIdString
	usersCantDelete?: boolean
}

export type ProductOptionGroupsRecord = {
	created: IsoAutoDateString
	displayText?: string
	id: string
	location?: RecordIdString
	name?: string
	updated: IsoAutoDateString
}

export type ProductOptionsSetupRecord = {
	created: IsoAutoDateString
	group?: RecordIdString
	id: string
	inactive?: boolean
	location?: RecordIdString
	name?: string
	optionProduct?: RecordIdString
	optionQty?: number
	sellPrice?: number
	sortOrder?: number
	updated: IsoAutoDateString
}

export type ProductsRecord = {
	allowCustomPrice?: boolean
	barcode?: string
	btnBackColor?: string
	btnTextColor?: string
	canBuyInKiosk?: boolean
	canBuyInWebshop?: boolean
	canPayInStore?: boolean
	category?: RecordIdString
	costPrice?: number
	created: IsoAutoDateString
	createdBy?: RecordIdString
	description?: string
	event?: RecordIdString
	eventBarValue?: number
	eventDate?: IsoDateString
	eventTicketLimit?: number
	hiredOutItem?: boolean
	id: string
	inStock?: number
	inactive?: boolean
	isServiceDiscount?: boolean
	location?: RecordIdString
	maxDiscount?: number
	memberBookingDiscountApplies?: boolean
	memberProductDiscountApplies?: boolean
	minStock?: number
	name?: string
	notDirectlySold?: boolean
	orderMultiple?: number
	preferredSupplier?: RecordIdString
	preventLoyalty?: boolean
	productImage?: FileNameString
	reportingGroup?: RecordIdString
	salesEnd?: IsoDateString
	sellPrice?: number
	sortOrder?: number
	subCategory?: RecordIdString
	suppliers?: RecordIdString[]
	updated: IsoAutoDateString
	updatedBy?: RecordIdString
}

export type ProductsOptionsRecord = {
	created: IsoAutoDateString
	id: string
	maxSelection?: number
	minSelection?: number
	optionGroup?: RecordIdString
	options?: RecordIdString[]
	product?: RecordIdString
	sortOrder?: number
	updated: IsoAutoDateString
}

export type ProductSubCategoriesRecord = {
	TODPricing?: RecordIdString[]
	category?: RecordIdString
	created: IsoAutoDateString
	createdBy?: RecordIdString
	id: string
	location?: RecordIdString
	name?: string
	prepArea?: RecordIdString
	sortOrder?: number
	updated: IsoAutoDateString
	updatedBy?: RecordIdString
}

export const QrGeneratorsTypeOptions = {
	"URL": "URL",
	"Email": "Email",
} as const
export type QrGeneratorsTypeOptions = typeof QrGeneratorsTypeOptions[keyof typeof QrGeneratorsTypeOptions]
export type QrGeneratorsRecord = {
	created: IsoAutoDateString
	data?: string
	email?: string
	emailed?: boolean
	id: string
	location?: RecordIdString
	printed?: boolean
	printer?: RecordIdString
	requiresEmail?: boolean
	requiresPrinting?: boolean
	title?: string
	type?: QrGeneratorsTypeOptions
	updated: IsoAutoDateString
}

export const RecurringBookingsIntervalOptions = {
	"Weeks": "Weeks",
	"Months": "Months",
} as const
export type RecurringBookingsIntervalOptions = typeof RecurringBookingsIntervalOptions[keyof typeof RecurringBookingsIntervalOptions]
export type RecurringBookingsRecord = {
	bookings?: RecordIdString[]
	created: IsoAutoDateString
	days?: string
	endDate?: IsoDateString
	frequency?: number
	id: string
	interval?: RecurringBookingsIntervalOptions
	updated: IsoAutoDateString
}

export const RecurringEventsIntervalOptions = {
	"Weeks": "Weeks",
	"Months": "Months",
} as const
export type RecurringEventsIntervalOptions = typeof RecurringEventsIntervalOptions[keyof typeof RecurringEventsIntervalOptions]
export type RecurringEventsRecord = {
	created: IsoAutoDateString
	days?: string
	endDate?: IsoDateString
	events?: RecordIdString[]
	frequency?: number
	id: string
	interval?: RecurringEventsIntervalOptions
	updated: IsoAutoDateString
}

export type ReferrersRecord = {
	created: IsoAutoDateString
	enabled?: boolean
	id: string
	name?: string
	notes?: string
	updated: IsoAutoDateString
}

export const ReportGenReportOptions = {
	"Staffing": "Staffing",
	"Bookings": "Bookings",
	"Shift Log": "Shift Log",
	"Sales": "Sales",
	"CheckLists": "CheckLists",
} as const
export type ReportGenReportOptions = typeof ReportGenReportOptions[keyof typeof ReportGenReportOptions]
export type ReportGenRecord = {
	created: IsoAutoDateString
	done?: boolean
	email?: string
	endTime?: IsoDateString
	id: string
	includeCSV?: boolean
	report?: ReportGenReportOptions
	result?: string
	startTime?: IsoDateString
	updated: IsoAutoDateString
}

export type ReportingGroupsRecord = {
	created: IsoAutoDateString
	id: string
	name?: string
	updated: IsoAutoDateString
}

export const ReportValuesPeriodOptions = {
	"Day": "Day",
	"Week": "Week",
	"Month": "Month",
} as const
export type ReportValuesPeriodOptions = typeof ReportValuesPeriodOptions[keyof typeof ReportValuesPeriodOptions]
export type ReportValuesRecord = {
	created: IsoAutoDateString
	date?: IsoDateString
	id: string
	period?: ReportValuesPeriodOptions
	premiumBayBooked?: number
	premiumBaySessions?: number
	productSales?: number
	rangeBayBooked?: number
	rangeBaySessions?: number
	servicesSales?: number
	updated: IsoAutoDateString
}

export const RolesUsersOptions = {
	"None": "None",
	"View": "View",
	"Edit": "Edit",
	"Create": "Create",
} as const
export type RolesUsersOptions = typeof RolesUsersOptions[keyof typeof RolesUsersOptions]

export const RolesReportingOptions = {
	"None": "None",
	"View": "View",
	"Edit": "Edit",
	"Create": "Create",
} as const
export type RolesReportingOptions = typeof RolesReportingOptions[keyof typeof RolesReportingOptions]

export const RolesBookingsOptions = {
	"None": "None",
	"View": "View",
	"Edit": "Edit",
	"Create": "Create",
} as const
export type RolesBookingsOptions = typeof RolesBookingsOptions[keyof typeof RolesBookingsOptions]

export const RolesRolesOptions = {
	"None": "None",
	"View": "View",
	"Assign": "Assign",
	"Edit": "Edit",
	"Create": "Create",
} as const
export type RolesRolesOptions = typeof RolesRolesOptions[keyof typeof RolesRolesOptions]

export const RolesActivitiesOptions = {
	"None": "None",
	"View": "View",
	"Edit": "Edit",
	"Create": "Create",
} as const
export type RolesActivitiesOptions = typeof RolesActivitiesOptions[keyof typeof RolesActivitiesOptions]

export const RolesGiftVouchersOptions = {
	"None": "None",
	"View": "View",
	"Assign": "Assign",
	"Edit": "Edit",
	"Create": "Create",
} as const
export type RolesGiftVouchersOptions = typeof RolesGiftVouchersOptions[keyof typeof RolesGiftVouchersOptions]

export const RolesCouponsOptions = {
	"None": "None",
	"View": "View",
	"Assign": "Assign",
	"Edit": "Edit",
	"Create": "Create",
} as const
export type RolesCouponsOptions = typeof RolesCouponsOptions[keyof typeof RolesCouponsOptions]

export const RolesAreasOptions = {
	"None": "None",
	"View": "View",
	"Edit": "Edit",
	"Create": "Create",
} as const
export type RolesAreasOptions = typeof RolesAreasOptions[keyof typeof RolesAreasOptions]

export const RolesDoorsOptions = {
	"None": "None",
	"View": "View",
	"Edit": "Edit",
	"Create": "Create",
} as const
export type RolesDoorsOptions = typeof RolesDoorsOptions[keyof typeof RolesDoorsOptions]

export const RolesMembershipsOptions = {
	"None": "None",
	"View": "View",
	"Assign": "Assign",
	"Edit": "Edit",
	"Create": "Create",
} as const
export type RolesMembershipsOptions = typeof RolesMembershipsOptions[keyof typeof RolesMembershipsOptions]

export const RolesLocationsOptions = {
	"None": "None",
	"View": "View",
	"Edit": "Edit",
	"Create": "Create",
} as const
export type RolesLocationsOptions = typeof RolesLocationsOptions[keyof typeof RolesLocationsOptions]

export const RolesDocketsOptions = {
	"None": "None",
	"View": "View",
	"Edit": "Edit",
	"Create": "Create",
} as const
export type RolesDocketsOptions = typeof RolesDocketsOptions[keyof typeof RolesDocketsOptions]

export const RolesProductsOptions = {
	"None": "None",
	"View": "View",
	"Edit": "Edit",
	"Create": "Create",
} as const
export type RolesProductsOptions = typeof RolesProductsOptions[keyof typeof RolesProductsOptions]

export const RolesFaqsOptions = {
	"None": "None",
	"View": "View",
	"Edit": "Edit",
	"Create": "Create",
} as const
export type RolesFaqsOptions = typeof RolesFaqsOptions[keyof typeof RolesFaqsOptions]

export const RolesEventsOptions = {
	"None": "None",
	"View": "View",
	"Edit": "Edit",
	"Create": "Create",
} as const
export type RolesEventsOptions = typeof RolesEventsOptions[keyof typeof RolesEventsOptions]

export const RolesDiscountCodesOptions = {
	"None": "None",
	"View": "View",
	"Edit": "Edit",
	"Create": "Create",
} as const
export type RolesDiscountCodesOptions = typeof RolesDiscountCodesOptions[keyof typeof RolesDiscountCodesOptions]

export const RolesSettingsOptions = {
	"None": "None",
	"View": "View",
	"Edit": "Edit",
	"Create": "Create",
} as const
export type RolesSettingsOptions = typeof RolesSettingsOptions[keyof typeof RolesSettingsOptions]

export const RolesAreaStatusOptions = {
	"None": "None",
	"View": "View",
	"Edit": "Edit",
	"Create": "Create",
} as const
export type RolesAreaStatusOptions = typeof RolesAreaStatusOptions[keyof typeof RolesAreaStatusOptions]

export const RolesMatrixDisplaysOptions = {
	"None": "None",
	"View": "View",
	"Edit": "Edit",
	"Create": "Create",
} as const
export type RolesMatrixDisplaysOptions = typeof RolesMatrixDisplaysOptions[keyof typeof RolesMatrixDisplaysOptions]

export const RolesPosCashOptions = {
	"None": "None",
	"View": "View",
	"Edit": "Edit",
	"Create": "Create",
} as const
export type RolesPosCashOptions = typeof RolesPosCashOptions[keyof typeof RolesPosCashOptions]

export const RolesAdvertisingOptions = {
	"None": "None",
	"View": "View",
	"Edit": "Edit",
	"Create": "Create",
} as const
export type RolesAdvertisingOptions = typeof RolesAdvertisingOptions[keyof typeof RolesAdvertisingOptions]

export const RolesEmailTemplatesOptions = {
	"None": "None",
	"View": "View",
	"Edit": "Edit",
	"Create": "Create",
} as const
export type RolesEmailTemplatesOptions = typeof RolesEmailTemplatesOptions[keyof typeof RolesEmailTemplatesOptions]

export const RolesStaffingOptions = {
	"None": "None",
	"View": "View",
	"Edit": "Edit",
	"Create": "Create",
} as const
export type RolesStaffingOptions = typeof RolesStaffingOptions[keyof typeof RolesStaffingOptions]

export const RolesActivityReportingOptions = {
	"None": "None",
	"View": "View",
	"Edit": "Edit",
	"Create": "Create",
} as const
export type RolesActivityReportingOptions = typeof RolesActivityReportingOptions[keyof typeof RolesActivityReportingOptions]

export const RolesPrepsOptions = {
	"None": "None",
	"View": "View",
	"Edit": "Edit",
	"Create": "Create",
} as const
export type RolesPrepsOptions = typeof RolesPrepsOptions[keyof typeof RolesPrepsOptions]
export type RolesRecord = {
	activities?: RolesActivitiesOptions
	activityReporting?: RolesActivityReportingOptions
	advertising?: RolesAdvertisingOptions
	areaStatus?: RolesAreaStatusOptions
	areas?: RolesAreasOptions
	bookings?: RolesBookingsOptions
	canSeeAdmin?: boolean
	coupons?: RolesCouponsOptions
	created: IsoAutoDateString
	createdBy?: RecordIdString
	description?: string
	discountCodes?: RolesDiscountCodesOptions
	dockets?: RolesDocketsOptions
	doors?: RolesDoorsOptions
	emailTemplates?: RolesEmailTemplatesOptions
	events?: RolesEventsOptions
	faqs?: RolesFaqsOptions
	giftVouchers?: RolesGiftVouchersOptions
	id: string
	location?: RecordIdString
	locations?: RolesLocationsOptions
	matrixDisplays?: RolesMatrixDisplaysOptions
	memberships?: RolesMembershipsOptions
	name?: string
	posCash?: RolesPosCashOptions
	preps?: RolesPrepsOptions
	products?: RolesProductsOptions
	reporting?: RolesReportingOptions
	roles?: RolesRolesOptions
	settings?: RolesSettingsOptions
	staffing?: RolesStaffingOptions
	updated: IsoAutoDateString
	updatedBy?: RecordIdString
	users?: RolesUsersOptions
}

export const SendQueueSendViaOptions = {
	"email": "email",
	"sms": "sms",
} as const
export type SendQueueSendViaOptions = typeof SendQueueSendViaOptions[keyof typeof SendQueueSendViaOptions]

export const SendQueueTypeOptions = {
	"Booking": "Booking",
} as const
export type SendQueueTypeOptions = typeof SendQueueTypeOptions[keyof typeof SendQueueTypeOptions]

export const SendQueueStatusOptions = {
	"New": "New",
	"Processing": "Processing",
	"Done": "Done",
} as const
export type SendQueueStatusOptions = typeof SendQueueStatusOptions[keyof typeof SendQueueStatusOptions]
export type SendQueueRecord = {
	action?: string
	address?: string
	created: IsoAutoDateString
	id: string
	processed?: IsoDateString
	recordID?: string
	sendVia?: SendQueueSendViaOptions
	status?: SendQueueStatusOptions
	type?: SendQueueTypeOptions
	updated: IsoAutoDateString
	user?: RecordIdString
}

export type ShiftLogRecord = {
	closeListCompleted?: IsoDateString
	closeListDuration?: number
	created: IsoAutoDateString
	day?: IsoDateString
	id: string
	location?: RecordIdString
	message?: string
	openListCompleted?: IsoDateString
	openListDuration?: number
	updated: IsoAutoDateString
	users?: RecordIdString[]
}

export type SiteCommsRecord = {
	action?: string
	created: IsoAutoDateString
	id: string
	updated: IsoAutoDateString
	var1?: string
}

export const StaffChecklistsListOptions = {
	"Open": "Open",
	"Close": "Close",
} as const
export type StaffChecklistsListOptions = typeof StaffChecklistsListOptions[keyof typeof StaffChecklistsListOptions]
export type StaffChecklistsRecord = {
	created: IsoAutoDateString
	description?: string
	id: string
	list?: StaffChecklistsListOptions
	location?: RecordIdString
	setupID?: RecordIdString
	updated: IsoAutoDateString
}

export const StaffChecklistSetupTypeOptions = {
	"Open": "Open",
	"Close": "Close",
	"Both": "Both",
} as const
export type StaffChecklistSetupTypeOptions = typeof StaffChecklistSetupTypeOptions[keyof typeof StaffChecklistSetupTypeOptions]
export type StaffChecklistSetupRecord = {
	created: IsoAutoDateString
	description?: string
	enabled?: boolean
	id: string
	location?: RecordIdString
	sortOrder?: number
	type?: StaffChecklistSetupTypeOptions
	updated: IsoAutoDateString
}

export type StaffLogRecord = {
	created: IsoAutoDateString
	endTime?: IsoDateString
	id: string
	location?: RecordIdString
	startTime?: IsoDateString
	updated: IsoAutoDateString
	user?: RecordIdString
}

export type StockAdjustLinesRecord = {
	created: IsoAutoDateString
	id: string
	price?: number
	product?: RecordIdString
	qty?: number
	stockAdjustment?: RecordIdString
	updated: IsoAutoDateString
}

export const StockAdjustmentsSourceOptions = {
	"Manual Adjustment": "Manual Adjustment",
	"Ext Software": "Ext Software",
} as const
export type StockAdjustmentsSourceOptions = typeof StockAdjustmentsSourceOptions[keyof typeof StockAdjustmentsSourceOptions]
export type StockAdjustmentsRecord = {
	created: IsoAutoDateString
	description?: string
	extID?: string
	extSource?: string
	id: string
	processed?: boolean
	source?: StockAdjustmentsSourceOptions
	timeApplied?: IsoDateString
	updated: IsoAutoDateString
}

export type SuppliersRecord = {
	accountsEmail?: string
	created: IsoAutoDateString
	createdBy?: RecordIdString
	id: string
	name?: string
	orderEmail?: string
	phone?: string
	updated: IsoAutoDateString
	updatedBy?: RecordIdString
}

export const TemplatesMsgTypeOptions = {
	"Email": "Email",
	"SMS": "SMS",
} as const
export type TemplatesMsgTypeOptions = typeof TemplatesMsgTypeOptions[keyof typeof TemplatesMsgTypeOptions]
export type TemplatesRecord = {
	created: IsoAutoDateString
	id: string
	images?: FileNameString[]
	msgType?: TemplatesMsgTypeOptions
	name?: string
	subjectHeader?: string
	templateText?: string
	updated: IsoAutoDateString
}

export type TimesRecord = {
	allMembers?: boolean
	allUsers?: boolean
	allowGuests?: boolean
	created: IsoAutoDateString
	createdBy?: RecordIdString
	finishHour?: number
	finishMin?: number
	friday?: boolean
	id: string
	location?: RecordIdString
	memberships?: RecordIdString[]
	monday?: boolean
	name?: string
	saturday?: boolean
	sortOrder?: number
	startHour?: number
	startMin?: number
	sunday?: boolean
	thursday?: boolean
	tuesday?: boolean
	updated: IsoAutoDateString
	updatedBy?: RecordIdString
	validFrom?: IsoDateString
	validTill?: IsoDateString
	wednesday?: boolean
}

export const TODPricingAppliesToOptions = {
	"Everyone": "Everyone",
	"Users": "Users",
	"Memberships": "Memberships",
	"No One": "No One",
} as const
export type TODPricingAppliesToOptions = typeof TODPricingAppliesToOptions[keyof typeof TODPricingAppliesToOptions]
export type TODPricingRecord = {
	appliesTo?: TODPricingAppliesToOptions
	created: IsoAutoDateString
	finishHour?: number
	finishMin?: number
	friday?: boolean
	id: string
	location?: RecordIdString
	monday?: boolean
	name?: string
	percentChange?: number
	saturday?: boolean
	startHour?: number
	startMin?: number
	sunday?: boolean
	thursday?: boolean
	tuesday?: boolean
	updated: IsoAutoDateString
	validFrom?: IsoDateString
	validTill?: IsoDateString
	wednesday?: boolean
}

export type TrackingRecord = {
	created: IsoAutoDateString
	description?: string
	field?: number
	id: string
	name?: string
	updated: IsoAutoDateString
}

export const TransactionsTransactionTypeOptions = {
	"Credit": "Credit",
	"Charge": "Charge",
} as const
export type TransactionsTransactionTypeOptions = typeof TransactionsTransactionTypeOptions[keyof typeof TransactionsTransactionTypeOptions]

export const TransactionsValueTypeOptions = {
	"Amount": "Amount",
	"Coupons": "Coupons",
	"Loyalty": "Loyalty",
	"Qty": "Qty",
} as const
export type TransactionsValueTypeOptions = typeof TransactionsValueTypeOptions[keyof typeof TransactionsValueTypeOptions]
export type TransactionsRecord = {
	amount?: number
	balance?: number
	coupon?: RecordIdString
	created: IsoAutoDateString
	description?: string
	docket?: RecordIdString
	docketLine?: RecordIdString
	docketPayment?: RecordIdString
	eventTicket?: RecordIdString
	giftVoucher?: RecordIdString
	id: string
	posCashInTill?: RecordIdString
	transactionType?: TransactionsTransactionTypeOptions
	updated: IsoAutoDateString
	user?: RecordIdString
	valueType?: TransactionsValueTypeOptions
}

export const UserCommunicationsMsgTypeOptions = {
	"Email": "Email",
	"SMS": "SMS",
} as const
export type UserCommunicationsMsgTypeOptions = typeof UserCommunicationsMsgTypeOptions[keyof typeof UserCommunicationsMsgTypeOptions]

export const UserCommunicationsStatusOptions = {
	"New": "New",
	"Sent": "Sent",
	"Delivered": "Delivered",
	"Opened": "Opened",
} as const
export type UserCommunicationsStatusOptions = typeof UserCommunicationsStatusOptions[keyof typeof UserCommunicationsStatusOptions]
export type UserCommunicationsRecord = {
	booking?: RecordIdString
	created: IsoAutoDateString
	from?: string
	id: string
	incoming?: boolean
	message?: string
	msgType?: UserCommunicationsMsgTypeOptions
	status?: UserCommunicationsStatusOptions
	subject?: string
	template?: RecordIdString
	to?: string
	updated: IsoAutoDateString
	user?: RecordIdString
}

export type UserMembershipsRecord = {
	created: IsoAutoDateString
	id: string
	initiatedDate?: IsoDateString
	membership?: RecordIdString
	membershipEnd?: IsoDateString
	membershipPauseEnd?: IsoDateString
	membershipPauseStart?: IsoDateString
	membershipPaused?: boolean
	membershipRenewDate?: IsoDateString
	membershipStart?: IsoDateString
	membershipStartedDate?: IsoDateString
	membershipUnPause?: boolean
	renewIssue?: string
	renewMembership?: RecordIdString
	updated: IsoAutoDateString
	user?: RecordIdString
}

export const UsersStatusOptions = {
	"Pending": "Pending",
	"New": "New",
	"Active": "Active",
	"Suspended": "Suspended",
	"Inactive": "Inactive",
	"Validating": "Validating",
	"Verifying": "Verifying",
	"Imported": "Imported",
	"ImpValidating": "ImpValidating",
	"ImpVerifying": "ImpVerifying",
	"ImpCompleted": "ImpCompleted",
	"ValidatingMobile": "ValidatingMobile",
	"ExtAuth": "ExtAuth",
	"Invited": "Invited",
} as const
export type UsersStatusOptions = typeof UsersStatusOptions[keyof typeof UsersStatusOptions]

export const UsersGenderOptions = {
	"Male": "Male",
	"Female": "Female",
	"Other": "Other",
	"Unknown": "Unknown",
} as const
export type UsersGenderOptions = typeof UsersGenderOptions[keyof typeof UsersGenderOptions]

export const UsersPreferedThemeOptions = {
	"Light": "Light",
	"Dark": "Dark",
	"System": "System",
	"As Selected": "As Selected",
} as const
export type UsersPreferedThemeOptions = typeof UsersPreferedThemeOptions[keyof typeof UsersPreferedThemeOptions]
export type UsersRecord<Tnotifications = unknown> = {
	acceptedMembersTCs?: IsoDateString
	acceptedTCs?: IsoDateString
	addCardTransactionReff?: string
	addCardWebRedirect?: string
	addCreditCard?: boolean
	address?: string
	allowConnectionsDetailsAccess?: boolean
	allowCouponTransfer?: boolean
	askedAboutNewsletters?: boolean
	avatar?: FileNameString
	city?: string
	color?: string
	connectionLimit?: number
	connections?: RecordIdString[]
	country?: string
	created: IsoAutoDateString
	debugUser?: boolean
	doMembershipCheckNow?: boolean
	dob?: IsoDateString
	dontDailySettleAccount?: boolean
	dontUseLoyaltyFirst?: boolean
	email: string
	emailVisibility?: boolean
	extID?: string
	extSource?: string
	failedPayments?: number
	firstName?: string
	fob?: string
	gender?: UsersGenderOptions
	hadJoinBonus?: boolean
	homeLocation?: RecordIdString
	id: string
	impPass?: string
	initiatedDate?: IsoDateString
	lastBooking?: IsoDateString
	lastLogin?: IsoDateString
	lastName?: string
	leftHanded?: boolean
	loyaltyBalance?: number
	mailchimpSyncError?: boolean
	membership?: RecordIdString
	membershipEnd?: IsoDateString
	membershipPauseEnd?: IsoDateString
	membershipPauseStart?: IsoDateString
	membershipPaused?: boolean
	membershipRenewDate?: IsoDateString
	membershipStart?: IsoDateString
	membershipStartedDate?: IsoDateString
	membershipUnPause?: boolean
	mobileNumber?: string
	mobileValidated?: boolean
	nationalBodyID?: string
	newEmail?: string
	notes?: string
	notifications?: null | Tnotifications
	paidJoinFee?: boolean
	password: string
	pmtId?: string
	pmtSource?: string
	postcode?: string
	preferedTheme?: UsersPreferedThemeOptions
	preventBenefits?: boolean
	qrlogin?: string
	referrer?: string
	rememberMe?: boolean
	renewIssue?: string
	renewMembership?: RecordIdString
	roles?: RecordIdString[]
	sendWelcomeEmail?: boolean
	settleAccountBalanceNow?: boolean
	showInDirectory?: boolean
	signupDate?: IsoDateString
	state?: string
	status?: UsersStatusOptions
	suburb?: string
	suspensionReason?: string
	tempBalance?: number
	tokenKey: string
	twoFA?: boolean
	twoFACode?: string
	updated: IsoAutoDateString
	updatedBy?: RecordIdString
	username: string
	verified?: boolean
	webResponseLoc?: string
	welcomeEmailSent?: boolean
}

// Response types include system fields and match responses from the PocketBase API
export type AuthoriginsResponse<Texpand = unknown> = Required<AuthoriginsRecord> & BaseSystemFields<Texpand>
export type ExternalauthsResponse<Texpand = unknown> = Required<ExternalauthsRecord> & BaseSystemFields<Texpand>
export type MfasResponse<Texpand = unknown> = Required<MfasRecord> & BaseSystemFields<Texpand>
export type OtpsResponse<Texpand = unknown> = Required<OtpsRecord> & BaseSystemFields<Texpand>
export type SuperusersResponse<Texpand = unknown> = Required<SuperusersRecord> & AuthSystemFields<Texpand>
export type AccessFOBsResponse<Texpand = unknown> = Required<AccessFOBsRecord> & BaseSystemFields<Texpand>
export type AccessReadersResponse<Texpand = unknown> = Required<AccessReadersRecord> & BaseSystemFields<Texpand>
export type ActivitiesResponse<Texpand = unknown> = Required<ActivitiesRecord> & BaseSystemFields<Texpand>
export type AdvertisingResponse<Texpand = unknown> = Required<AdvertisingRecord> & BaseSystemFields<Texpand>
export type AlarmsResponse<Texpand = unknown> = Required<AlarmsRecord> & BaseSystemFields<Texpand>
export type AreasResponse<Texpand = unknown> = Required<AreasRecord> & BaseSystemFields<Texpand>
export type AreaStatusResponse<Texpand = unknown> = Required<AreaStatusRecord> & BaseSystemFields<Texpand>
export type BankCardsResponse<Texpand = unknown> = Required<BankCardsRecord> & BaseSystemFields<Texpand>
export type BarcodeReadersResponse<Texpand = unknown> = Required<BarcodeReadersRecord> & BaseSystemFields<Texpand>
export type BayControlSetupsResponse<Texpand = unknown> = Required<BayControlSetupsRecord> & BaseSystemFields<Texpand>
export type BookingHistoryResponse<Texpand = unknown> = Required<BookingHistoryRecord> & BaseSystemFields<Texpand>
export type BookingsResponse<Texpand = unknown> = Required<BookingsRecord> & BaseSystemFields<Texpand>
export type CartItemOptionsResponse<Texpand = unknown> = Required<CartItemOptionsRecord> & BaseSystemFields<Texpand>
export type CartItemsResponse<Texpand = unknown> = Required<CartItemsRecord> & BaseSystemFields<Texpand>
export type CartPaymentsResponse<Texpand = unknown> = Required<CartPaymentsRecord> & BaseSystemFields<Texpand>
export type CartsResponse<Texpand = unknown> = Required<CartsRecord> & BaseSystemFields<Texpand>
export type ChallengesResponse<Texpand = unknown> = Required<ChallengesRecord> & BaseSystemFields<Texpand>
export type CouponRestrictTimesResponse<Texpand = unknown> = Required<CouponRestrictTimesRecord> & BaseSystemFields<Texpand>
export type CouponsResponse<Texpand = unknown> = Required<CouponsRecord> & BaseSystemFields<Texpand>
export type CouponTypesResponse<Texpand = unknown> = Required<CouponTypesRecord> & BaseSystemFields<Texpand>
export type DebugLogsResponse<Texpand = unknown> = Required<DebugLogsRecord> & BaseSystemFields<Texpand>
export type DiscountCodesResponse<Texpand = unknown> = Required<DiscountCodesRecord> & BaseSystemFields<Texpand>
export type DocketLineOptionsResponse<Texpand = unknown> = Required<DocketLineOptionsRecord> & BaseSystemFields<Texpand>
export type DocketLinesResponse<Texpand = unknown> = Required<DocketLinesRecord> & BaseSystemFields<Texpand>
export type DocketLineTransactionsResponse<Texpand = unknown> = Required<DocketLineTransactionsRecord> & BaseSystemFields<Texpand>
export type DocketPaymentsResponse<Texpand = unknown> = Required<DocketPaymentsRecord> & BaseSystemFields<Texpand>
export type DocketReceiptsResponse<Texpand = unknown> = Required<DocketReceiptsRecord> & BaseSystemFields<Texpand>
export type DocketsResponse<Texpand = unknown> = Required<DocketsRecord> & BaseSystemFields<Texpand>
export type DoorsResponse<Texpand = unknown> = Required<DoorsRecord> & BaseSystemFields<Texpand>
export type EventOptionsResponse<Texpand = unknown> = Required<EventOptionsRecord> & BaseSystemFields<Texpand>
export type EventsResponse<Texpand = unknown> = Required<EventsRecord> & BaseSystemFields<Texpand>
export type EventTicketsResponse<Toptions = unknown, Texpand = unknown> = Required<EventTicketsRecord<Toptions>> & BaseSystemFields<Texpand>
export type ExternalAppsResponse<Texpand = unknown> = Required<ExternalAppsRecord> & BaseSystemFields<Texpand>
export type FaqResponse<Texpand = unknown> = Required<FaqRecord> & BaseSystemFields<Texpand>
export type FaqTopicsResponse<Texpand = unknown> = Required<FaqTopicsRecord> & BaseSystemFields<Texpand>
export type GiftVouchersResponse<Texpand = unknown> = Required<GiftVouchersRecord> & BaseSystemFields<Texpand>
export type GlobalSetupResponse<Texpand = unknown> = Required<GlobalSetupRecord> & BaseSystemFields<Texpand>
export type GuestUsersResponse<Texpand = unknown> = Required<GuestUsersRecord> & BaseSystemFields<Texpand>
export type HomeAlertsResponse<Texpand = unknown> = Required<HomeAlertsRecord> & BaseSystemFields<Texpand>
export type IntegrationsResponse<TmailchimpAvailableAudiences = unknown, TreckonAvailableBooks = unknown, Texpand = unknown> = Required<IntegrationsRecord<TmailchimpAvailableAudiences, TreckonAvailableBooks>> & BaseSystemFields<Texpand>
export type IoNodesResponse<Texpand = unknown> = Required<IoNodesRecord> & BaseSystemFields<Texpand>
export type KiosksResponse<Texpand = unknown> = Required<KiosksRecord> & BaseSystemFields<Texpand>
export type LocationsResponse<Texpand = unknown> = Required<LocationsRecord> & BaseSystemFields<Texpand>
export type LogsResponse<Texpand = unknown> = Required<LogsRecord> & BaseSystemFields<Texpand>
export type MatrixDevicesResponse<Texpand = unknown> = Required<MatrixDevicesRecord> & BaseSystemFields<Texpand>
export type MembershipCategoriesResponse<Texpand = unknown> = Required<MembershipCategoriesRecord> & BaseSystemFields<Texpand>
export type MembershipsResponse<Tbenefits = unknown, Texpand = unknown> = Required<MembershipsRecord<Tbenefits>> & BaseSystemFields<Texpand>
export type NotificationsResponse<Texpand = unknown> = Required<NotificationsRecord> & BaseSystemFields<Texpand>
export type OnlinePaymentRequestsResponse<Texpand = unknown> = Required<OnlinePaymentRequestsRecord> & BaseSystemFields<Texpand>
export type OutputsResponse<Texpand = unknown> = Required<OutputsRecord> & BaseSystemFields<Texpand>
export type PaymentTerminalsResponse<Texpand = unknown> = Required<PaymentTerminalsRecord> & BaseSystemFields<Texpand>
export type PosSetupsResponse<Texpand = unknown> = Required<PosSetupsRecord> & BaseSystemFields<Texpand>
export type PrepAreasResponse<Texpand = unknown> = Required<PrepAreasRecord> & BaseSystemFields<Texpand>
export type PrepLinesResponse<Texpand = unknown> = Required<PrepLinesRecord> & BaseSystemFields<Texpand>
export type PrepsResponse<Texpand = unknown> = Required<PrepsRecord> & BaseSystemFields<Texpand>
export type PrintersResponse<Texpand = unknown> = Required<PrintersRecord> & BaseSystemFields<Texpand>
export type ProductCategoriesResponse<Texpand = unknown> = Required<ProductCategoriesRecord> & BaseSystemFields<Texpand>
export type ProductOptionGroupsResponse<Texpand = unknown> = Required<ProductOptionGroupsRecord> & BaseSystemFields<Texpand>
export type ProductOptionsSetupResponse<Texpand = unknown> = Required<ProductOptionsSetupRecord> & BaseSystemFields<Texpand>
export type ProductsResponse<Texpand = unknown> = Required<ProductsRecord> & BaseSystemFields<Texpand>
export type ProductsOptionsResponse<Texpand = unknown> = Required<ProductsOptionsRecord> & BaseSystemFields<Texpand>
export type ProductSubCategoriesResponse<Texpand = unknown> = Required<ProductSubCategoriesRecord> & BaseSystemFields<Texpand>
export type QrGeneratorsResponse<Texpand = unknown> = Required<QrGeneratorsRecord> & BaseSystemFields<Texpand>
export type RecurringBookingsResponse<Texpand = unknown> = Required<RecurringBookingsRecord> & BaseSystemFields<Texpand>
export type RecurringEventsResponse<Texpand = unknown> = Required<RecurringEventsRecord> & BaseSystemFields<Texpand>
export type ReferrersResponse<Texpand = unknown> = Required<ReferrersRecord> & BaseSystemFields<Texpand>
export type ReportGenResponse<Texpand = unknown> = Required<ReportGenRecord> & BaseSystemFields<Texpand>
export type ReportingGroupsResponse<Texpand = unknown> = Required<ReportingGroupsRecord> & BaseSystemFields<Texpand>
export type ReportValuesResponse<Texpand = unknown> = Required<ReportValuesRecord> & BaseSystemFields<Texpand>
export type RolesResponse<Texpand = unknown> = Required<RolesRecord> & BaseSystemFields<Texpand>
export type SendQueueResponse<Texpand = unknown> = Required<SendQueueRecord> & BaseSystemFields<Texpand>
export type ShiftLogResponse<Texpand = unknown> = Required<ShiftLogRecord> & BaseSystemFields<Texpand>
export type SiteCommsResponse<Texpand = unknown> = Required<SiteCommsRecord> & BaseSystemFields<Texpand>
export type StaffChecklistsResponse<Texpand = unknown> = Required<StaffChecklistsRecord> & BaseSystemFields<Texpand>
export type StaffChecklistSetupResponse<Texpand = unknown> = Required<StaffChecklistSetupRecord> & BaseSystemFields<Texpand>
export type StaffLogResponse<Texpand = unknown> = Required<StaffLogRecord> & BaseSystemFields<Texpand>
export type StockAdjustLinesResponse<Texpand = unknown> = Required<StockAdjustLinesRecord> & BaseSystemFields<Texpand>
export type StockAdjustmentsResponse<Texpand = unknown> = Required<StockAdjustmentsRecord> & BaseSystemFields<Texpand>
export type SuppliersResponse<Texpand = unknown> = Required<SuppliersRecord> & BaseSystemFields<Texpand>
export type TemplatesResponse<Texpand = unknown> = Required<TemplatesRecord> & BaseSystemFields<Texpand>
export type TimesResponse<Texpand = unknown> = Required<TimesRecord> & BaseSystemFields<Texpand>
export type TODPricingResponse<Texpand = unknown> = Required<TODPricingRecord> & BaseSystemFields<Texpand>
export type TrackingResponse<Texpand = unknown> = Required<TrackingRecord> & BaseSystemFields<Texpand>
export type TransactionsResponse<Texpand = unknown> = Required<TransactionsRecord> & BaseSystemFields<Texpand>
export type UserCommunicationsResponse<Texpand = unknown> = Required<UserCommunicationsRecord> & BaseSystemFields<Texpand>
export type UserMembershipsResponse<Texpand = unknown> = Required<UserMembershipsRecord> & BaseSystemFields<Texpand>
export type UsersResponse<Tnotifications = unknown, Texpand = unknown> = Required<UsersRecord<Tnotifications>> & AuthSystemFields<Texpand>

// Types containing all Records and Responses, useful for creating typing helper functions

export type CollectionRecords = {
	_authOrigins: AuthoriginsRecord
	_externalAuths: ExternalauthsRecord
	_mfas: MfasRecord
	_otps: OtpsRecord
	_superusers: SuperusersRecord
	accessFOBs: AccessFOBsRecord
	accessReaders: AccessReadersRecord
	activities: ActivitiesRecord
	advertising: AdvertisingRecord
	alarms: AlarmsRecord
	areas: AreasRecord
	areaStatus: AreaStatusRecord
	bankCards: BankCardsRecord
	barcodeReaders: BarcodeReadersRecord
	bayControlSetups: BayControlSetupsRecord
	bookingHistory: BookingHistoryRecord
	bookings: BookingsRecord
	cartItemOptions: CartItemOptionsRecord
	cartItems: CartItemsRecord
	cartPayments: CartPaymentsRecord
	carts: CartsRecord
	challenges: ChallengesRecord
	couponRestrictTimes: CouponRestrictTimesRecord
	coupons: CouponsRecord
	couponTypes: CouponTypesRecord
	debugLogs: DebugLogsRecord
	discountCodes: DiscountCodesRecord
	docketLineOptions: DocketLineOptionsRecord
	docketLines: DocketLinesRecord
	docketLineTransactions: DocketLineTransactionsRecord
	docketPayments: DocketPaymentsRecord
	docketReceipts: DocketReceiptsRecord
	dockets: DocketsRecord
	doors: DoorsRecord
	eventOptions: EventOptionsRecord
	events: EventsRecord
	eventTickets: EventTicketsRecord
	externalApps: ExternalAppsRecord
	faq: FaqRecord
	faqTopics: FaqTopicsRecord
	giftVouchers: GiftVouchersRecord
	globalSetup: GlobalSetupRecord
	guestUsers: GuestUsersRecord
	homeAlerts: HomeAlertsRecord
	integrations: IntegrationsRecord
	ioNodes: IoNodesRecord
	kiosks: KiosksRecord
	locations: LocationsRecord
	logs: LogsRecord
	matrixDevices: MatrixDevicesRecord
	membershipCategories: MembershipCategoriesRecord
	memberships: MembershipsRecord
	notifications: NotificationsRecord
	onlinePaymentRequests: OnlinePaymentRequestsRecord
	outputs: OutputsRecord
	paymentTerminals: PaymentTerminalsRecord
	posSetups: PosSetupsRecord
	prepAreas: PrepAreasRecord
	prepLines: PrepLinesRecord
	preps: PrepsRecord
	printers: PrintersRecord
	productCategories: ProductCategoriesRecord
	productOptionGroups: ProductOptionGroupsRecord
	productOptionsSetup: ProductOptionsSetupRecord
	products: ProductsRecord
	productsOptions: ProductsOptionsRecord
	productSubCategories: ProductSubCategoriesRecord
	qrGenerators: QrGeneratorsRecord
	recurringBookings: RecurringBookingsRecord
	recurringEvents: RecurringEventsRecord
	referrers: ReferrersRecord
	reportGen: ReportGenRecord
	reportingGroups: ReportingGroupsRecord
	reportValues: ReportValuesRecord
	roles: RolesRecord
	sendQueue: SendQueueRecord
	shiftLog: ShiftLogRecord
	siteComms: SiteCommsRecord
	staffChecklists: StaffChecklistsRecord
	staffChecklistSetup: StaffChecklistSetupRecord
	staffLog: StaffLogRecord
	stockAdjustLines: StockAdjustLinesRecord
	stockAdjustments: StockAdjustmentsRecord
	suppliers: SuppliersRecord
	templates: TemplatesRecord
	times: TimesRecord
	TODPricing: TODPricingRecord
	tracking: TrackingRecord
	transactions: TransactionsRecord
	userCommunications: UserCommunicationsRecord
	userMemberships: UserMembershipsRecord
	users: UsersRecord
}

export type CollectionResponses = {
	_authOrigins: AuthoriginsResponse
	_externalAuths: ExternalauthsResponse
	_mfas: MfasResponse
	_otps: OtpsResponse
	_superusers: SuperusersResponse
	accessFOBs: AccessFOBsResponse
	accessReaders: AccessReadersResponse
	activities: ActivitiesResponse
	advertising: AdvertisingResponse
	alarms: AlarmsResponse
	areas: AreasResponse
	areaStatus: AreaStatusResponse
	bankCards: BankCardsResponse
	barcodeReaders: BarcodeReadersResponse
	bayControlSetups: BayControlSetupsResponse
	bookingHistory: BookingHistoryResponse
	bookings: BookingsResponse
	cartItemOptions: CartItemOptionsResponse
	cartItems: CartItemsResponse
	cartPayments: CartPaymentsResponse
	carts: CartsResponse
	challenges: ChallengesResponse
	couponRestrictTimes: CouponRestrictTimesResponse
	coupons: CouponsResponse
	couponTypes: CouponTypesResponse
	debugLogs: DebugLogsResponse
	discountCodes: DiscountCodesResponse
	docketLineOptions: DocketLineOptionsResponse
	docketLines: DocketLinesResponse
	docketLineTransactions: DocketLineTransactionsResponse
	docketPayments: DocketPaymentsResponse
	docketReceipts: DocketReceiptsResponse
	dockets: DocketsResponse
	doors: DoorsResponse
	eventOptions: EventOptionsResponse
	events: EventsResponse
	eventTickets: EventTicketsResponse
	externalApps: ExternalAppsResponse
	faq: FaqResponse
	faqTopics: FaqTopicsResponse
	giftVouchers: GiftVouchersResponse
	globalSetup: GlobalSetupResponse
	guestUsers: GuestUsersResponse
	homeAlerts: HomeAlertsResponse
	integrations: IntegrationsResponse
	ioNodes: IoNodesResponse
	kiosks: KiosksResponse
	locations: LocationsResponse
	logs: LogsResponse
	matrixDevices: MatrixDevicesResponse
	membershipCategories: MembershipCategoriesResponse
	memberships: MembershipsResponse
	notifications: NotificationsResponse
	onlinePaymentRequests: OnlinePaymentRequestsResponse
	outputs: OutputsResponse
	paymentTerminals: PaymentTerminalsResponse
	posSetups: PosSetupsResponse
	prepAreas: PrepAreasResponse
	prepLines: PrepLinesResponse
	preps: PrepsResponse
	printers: PrintersResponse
	productCategories: ProductCategoriesResponse
	productOptionGroups: ProductOptionGroupsResponse
	productOptionsSetup: ProductOptionsSetupResponse
	products: ProductsResponse
	productsOptions: ProductsOptionsResponse
	productSubCategories: ProductSubCategoriesResponse
	qrGenerators: QrGeneratorsResponse
	recurringBookings: RecurringBookingsResponse
	recurringEvents: RecurringEventsResponse
	referrers: ReferrersResponse
	reportGen: ReportGenResponse
	reportingGroups: ReportingGroupsResponse
	reportValues: ReportValuesResponse
	roles: RolesResponse
	sendQueue: SendQueueResponse
	shiftLog: ShiftLogResponse
	siteComms: SiteCommsResponse
	staffChecklists: StaffChecklistsResponse
	staffChecklistSetup: StaffChecklistSetupResponse
	staffLog: StaffLogResponse
	stockAdjustLines: StockAdjustLinesResponse
	stockAdjustments: StockAdjustmentsResponse
	suppliers: SuppliersResponse
	templates: TemplatesResponse
	times: TimesResponse
	TODPricing: TODPricingResponse
	tracking: TrackingResponse
	transactions: TransactionsResponse
	userCommunications: UserCommunicationsResponse
	userMemberships: UserMembershipsResponse
	users: UsersResponse
}

// Utility types for create/update operations

type ProcessCreateAndUpdateFields<T> = Omit<{
	// Omit AutoDate fields
	[K in keyof T as Extract<T[K], IsoAutoDateString> extends never ? K : never]: 
		// Convert FileNameString to File
		T[K] extends infer U ? 
			U extends (FileNameString | FileNameString[]) ? 
				U extends any[] ? File[] : File 
			: U
		: never
}, 'id'>

// Create type for Auth collections
export type CreateAuth<T> = {
	id?: RecordIdString
	email: string
	emailVisibility?: boolean
	password: string
	passwordConfirm: string
	verified?: boolean
} & ProcessCreateAndUpdateFields<T>

// Create type for Base collections
export type CreateBase<T> = {
	id?: RecordIdString
} & ProcessCreateAndUpdateFields<T>

// Update type for Auth collections
export type UpdateAuth<T> = Partial<
	Omit<ProcessCreateAndUpdateFields<T>, keyof AuthSystemFields>
> & {
	email?: string
	emailVisibility?: boolean
	oldPassword?: string
	password?: string
	passwordConfirm?: string
	verified?: boolean
}

// Update type for Base collections
export type UpdateBase<T> = Partial<
	Omit<ProcessCreateAndUpdateFields<T>, keyof BaseSystemFields>
>

// Get the correct create type for any collection
export type Create<T extends keyof CollectionResponses> =
	CollectionResponses[T] extends AuthSystemFields
		? CreateAuth<CollectionRecords[T]>
		: CreateBase<CollectionRecords[T]>

// Get the correct update type for any collection
export type Update<T extends keyof CollectionResponses> =
	CollectionResponses[T] extends AuthSystemFields
		? UpdateAuth<CollectionRecords[T]>
		: UpdateBase<CollectionRecords[T]>

// Type for usage with type asserted PocketBase instance
// https://github.com/pocketbase/js-sdk#specify-typescript-definitions

export type TypedPocketBase = {
	collection<T extends keyof CollectionResponses>(
		idOrName: T
	): RecordService<CollectionResponses[T]>
} & PocketBase
